import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { signInWithGoogle } from '@/lib/auth';
import { navigate, linkProps } from '@/lib/router';
import { Listing, ProductItem, RealEstateProject } from '@/lib/types';
import {
  Building2,
  Plus,
  BarChart3,
  Inbox,
  Mail,
  User,
  Phone,
  Lock,
  Globe,
  MapPin,
  Video,
  Trash2,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  LogOut,
  Info,
  Edit3,
  Sparkles,
  ExternalLink,
  Calendar,
  Tag,
  Image as ImageIcon,
  Search,
  ShieldCheck,
  Eye,
  EyeOff,
  ShoppingBag,
  Home
} from 'lucide-react';

interface Owner {
  email: string;
  name: string;
  phone: string;
  password?: string;
  activated: boolean;
  activation_otp: string;
}

interface SimulatedEmail {
  id: string;
  to: string;
  subject: string;
  body: string;
  sentAt: string;
}

interface OfferEvent {
  id: string;
  type: 'Offer' | 'Event';
  title: string;
  description: string;
  image: string | null;
  startDate: string;
  endDate: string;
  status: 'pending_approval' | 'approved';
  promo_code?: string;
  event_location?: string;
  event_map_link?: string;
}

import { INDIAN_CITIES, BUSINESS_CATEGORIES } from '@/lib/data_registry';
import { convertFileToWebP, convertMultipleFilesToWebP, validateAndReadBrochure } from '@/lib/image_utils';

const AMENITIES = [
  { id: 'wifi', label: 'Free Wi-Fi', icon: 'wifi' },
  { id: 'ac', label: 'Air Conditioning', icon: 'ac_unit' },
  { id: 'parking', label: 'Parking Space', icon: 'local_parking' },
  { id: 'accessible', label: 'Access Support', icon: 'accessible' },
  { id: 'pets', label: 'Pet Friendly', icon: 'pets' },
  { id: 'seating', label: 'Outdoor Seating', icon: 'deck' },
  { id: 'kids', label: 'Kids Friendly', icon: 'child_care' },
  { id: 'music', label: 'Live Music', icon: 'music_note' },
  { id: 'valet', label: 'Valet Parking', icon: 'directions_car' },
  { id: 'elevator', label: 'Elevator', icon: 'elevator' },
  { id: 'cards', label: 'Cards Accepted', icon: 'credit_card' },
  { id: 'upi', label: 'UPI / QR Payments', icon: 'qr_code' },
  { id: 'cash', label: 'Cash Only', icon: 'payments' },
  { id: 'delivery', label: 'Home Delivery', icon: 'local_shipping' },
  { id: 'takeaway', label: 'Takeaway Available', icon: 'shopping_bag' },
  { id: 'booking', label: 'Online Booking', icon: 'online_prediction' },
  { id: 'surveillance', label: 'CCTV Surveillance', icon: 'videocam' },
  { id: 'sanitized', label: 'Sanitized Spaces', icon: 'clean_hands' },
  { id: 'eco', label: 'Eco-Friendly', icon: 'eco' },
  { id: 'open24', label: 'Open 24/7', icon: 'schedule' }
];

export default function OwnerConsole({ onExit }: { onExit: () => void }) {
  const [user, setUser] = useState<Owner | null>(null);
  const [activeTab, setActiveTab] = useState<'analytics' | 'listings' | 'add' | 'offers' | 'products' | 'change_password'>('listings');

  // Product Showcase States
  const [prodBizId, setProdBizId] = useState('');
  const [prodTitle, setProdTitle] = useState('');
  const [prodCategory, setProdCategory] = useState('');
  const [prodPrice, setProdPrice] = useState('');
  const [prodOriginalPrice, setProdOriginalPrice] = useState('');
  const [prodImage, setProdImage] = useState<string | null>(null);
  const [prodDescription, setProdDescription] = useState('');
  const [prodFeatures, setProdFeatures] = useState('');
  const [prodInStock, setProdInStock] = useState(true);
  const [prodBadge, setProdBadge] = useState('Best Seller');
  const [prodSku, setProdSku] = useState('');
  const [prodWebsiteUrl, setProdWebsiteUrl] = useState('');
  const [editingProduct, setEditingProduct] = useState<ProductItem | null>(null);
  const [prodSuccess, setProdSuccess] = useState('');

  // Real Estate Project Showcase States
  const [projBizId, setProjBizId] = useState('');
  const [projName, setProjName] = useState('');
  const [projType, setProjType] = useState<'Apartments' | 'Luxury Villas' | 'Gated Community' | 'Plots' | 'Commercial'>('Apartments');
  const [projStatus, setProjStatus] = useState<'Pre-Launch' | 'Under Construction' | 'Ready to Move' | 'Completed'>('Under Construction');
  const [projLocation, setProjLocation] = useState('');
  const [projCity, setProjCity] = useState('');
  const [projPriceRange, setProjPriceRange] = useState('');
  const [projPossessionDate, setProjPossessionDate] = useState('');
  const [projReraId, setProjReraId] = useState('');
  const [projHeroImage, setProjHeroImage] = useState<string | null>(null);
  const [projGallery, setProjGallery] = useState<{ url: string; sizeKb?: number }[]>([]);
  const [projIsCompressing, setProjIsCompressing] = useState(false);
  const [projBrochureFileName, setProjBrochureFileName] = useState('');
  const [projBrochureSizeMb, setProjBrochureSizeMb] = useState<number | null>(null);
  const [projBrochureError, setProjBrochureError] = useState('');
  const [projAmenities, setProjAmenities] = useState('');
  const [projConfigs, setProjConfigs] = useState('');
  const [projWebsiteUrl, setProjWebsiteUrl] = useState('');
  const [projBrochureUrl, setProjBrochureUrl] = useState('');
  const [projYoutubeUrl, setProjYoutubeUrl] = useState('');
  const [projInstagramUrl, setProjInstagramUrl] = useState('');
  const [projFacebookUrl, setProjFacebookUrl] = useState('');
  const [projTowers, setProjTowers] = useState('4 Iconic Towers');
  const [projFloors, setProjFloors] = useState('19 Floors');
  const [projUnits, setProjUnits] = useState('468 Units');
  const [projTotalArea, setProjTotalArea] = useState('1.98 Acres');
  const [projOpenArea, setProjOpenArea] = useState('70% Greenery');
  const [projSpecStructure, setProjSpecStructure] = useState('');
  const [projSpecWallFinish, setProjSpecWallFinish] = useState('');
  const [projSpecFlooring, setProjSpecFlooring] = useState('');
  const [projSpecDoorsWindows, setProjSpecDoorsWindows] = useState('');
  const [projSpecElectrical, setProjSpecElectrical] = useState('');
  const [editingProject, setEditingProject] = useState<RealEstateProject | null>(null);
  const [projSuccess, setProjSuccess] = useState('');

  // Auth States
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');

  // Forgot Password & Reset Password States
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const [isResetPasswordMode, setIsResetPasswordMode] = useState(false);
  const [newResetPassword, setNewResetPassword] = useState('');
  const [confirmResetPassword, setConfirmResetPassword] = useState('');
  const [showNewResetPass, setShowNewResetPass] = useState(false);
  const [showConfirmResetPass, setShowConfirmResetPass] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // In-Dashboard Change Password States
  const [dashNewPass, setDashNewPass] = useState('');
  const [dashConfirmPass, setDashConfirmPass] = useState('');
  const [dashPassError, setDashPassError] = useState('');
  const [dashPassSuccess, setDashPassSuccess] = useState('');
  const [dashPassLoading, setDashPassLoading] = useState(false);

  // OTP Activation States
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [otpError, setOtpError] = useState('');
  const [activeOtpCode, setActiveOtpCode] = useState('');

  // Handle Resend OTP
  const handleResendOtp = async () => {
    const freshOtp = Math.floor(100000 + Math.random() * 900000).toString();
    setActiveOtpCode(freshOtp);
    const registry = await getOwnersRegistry();
    const foundIdx = registry.findIndex(o => o.email.toLowerCase() === otpEmail.toLowerCase());
    if (foundIdx !== -1) {
      registry[foundIdx].activation_otp = freshOtp;
      await saveOwnersRegistry(registry);
    }
    sendSimulatedEmail(
      otpEmail,
      'Your Zha Search Verification Code',
      `Your 6-digit activation OTP is: ${freshOtp}`
    );
    setAuthSuccess(`Fresh OTP code sent: ${freshOtp}`);
  };

  // Instant 1-Click Activate Helper for Testing
  const handleInstantActivate = async () => {
    const registry = await getOwnersRegistry();
    const foundIdx = registry.findIndex(o => o.email.toLowerCase() === (otpEmail || email).toLowerCase());
    if (foundIdx !== -1) {
      registry[foundIdx].activated = true;
      await saveOwnersRegistry(registry);
      const activeOwner = {
        email: registry[foundIdx].email,
        name: registry[foundIdx].name,
        phone: registry[foundIdx].phone,
        activated: true,
        activation_otp: ''
      };
      setUser(activeOwner);
      localStorage.setItem('owner_user', JSON.stringify(activeOwner));
      fetchListings(activeOwner.email);
    }
  };

  // Listings & Form States
  const [listings, setListings] = useState<Listing[]>([]);
  const [loadingListings, setLoadingListings] = useState(false);
  const [editingListing, setEditingListing] = useState<Listing | null>(null);

  // Form Fields
  const [bizName, setBizName] = useState('');
  const [bizCategory, setBizCategory] = useState('Restaurant');
  const [bizPhone, setBizPhone] = useState('');
  const [bizWhatsapp, setBizWhatsapp] = useState('');
  const [bizEmail, setBizEmail] = useState('');
  const [bizWebsite, setBizWebsite] = useState('');
  const [bizAddress, setBizAddress] = useState('');
  const [bizDescription, setBizDescription] = useState('');
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [googleMapLink, setGoogleMapLink] = useState('');
  const [youtubeLinks, setYoutubeLinks] = useState<string[]>(['']);
  const [socialLinks, setSocialLinks] = useState({
    facebook: '',
    twitter: '',
    instagram: '',
    linkedin: ''
  });
  const [logoImage, setLogoImage] = useState<string | null>(null);
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState(false);

  // Search/Dropdown States in Form
  const [categorySearch, setCategorySearch] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  // Offers & Events form states
  const [offerType, setOfferType] = useState<'Offer' | 'Event'>('Offer');
  const [editingOfferEvent, setEditingOfferEvent] = useState<OfferEvent | null>(null);
  const [offerTitle, setOfferTitle] = useState('');
  const [offerDesc, setOfferDesc] = useState('');
  const [offerImage, setOfferImage] = useState<string | null>(null);
  const [offerStartDate, setOfferStartDate] = useState('');
  const [offerEndDate, setOfferEndDate] = useState('');
  const [offerPromoCode, setOfferPromoCode] = useState('');
  const [eventLocationType, setEventLocationType] = useState<'business' | 'custom'>('business');
  const [eventLocation, setEventLocation] = useState('');
  const [eventMapLink, setEventMapLink] = useState('');
  const [selectedBizId, setSelectedBizId] = useState('');
  const [offerError, setOfferError] = useState('');
  const [offerSuccess, setOfferSuccess] = useState(false);

  // Shop Opening Hours State (Google Maps Style)
  const [openingHoursPreset, setOpeningHoursPreset] = useState('Mon - Sat (9:00 AM - 9:00 PM)');
  const [customHours, setCustomHours] = useState({
    Mon: '9:00 AM - 9:00 PM',
    Tue: '9:00 AM - 9:00 PM',
    Wed: '9:00 AM - 9:00 PM',
    Thu: '9:00 AM - 9:00 PM',
    Fri: '9:00 AM - 9:00 PM',
    Sat: '9:00 AM - 9:00 PM',
    Sun: 'Closed'
  });

  // Email notifications log
  const [emails, setEmails] = useState<SimulatedEmail[]>([]);

  // Persistent registry load/save via Supabase Settings
  const getOwnersRegistry = async (): Promise<Owner[]> => {
    try {
      const { data } = await supabase.from('settings').select('*').eq('key', 'owners_registry').maybeSingle();
      if (data && data.value && Array.isArray(data.value.owners)) {
        return data.value.owners as Owner[];
      }
    } catch (e) {
      console.error(e);
    }
    return [];
  };

  const saveOwnersRegistry = async (owners: Owner[]) => {
    try {
      await supabase.from('settings').upsert({ key: 'owners_registry', value: { owners } });
    } catch (e) {
      console.error(e);
    }
  };

  // Simulated Email Helper
  const sendSimulatedEmail = (to: string, subject: string, body: string) => {
    const newEmail: SimulatedEmail = {
      id: Math.random().toString(36).substring(2),
      to,
      subject,
      body,
      sentAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ' ' + new Date().toLocaleDateString()
    };
    const updated = [newEmail, ...emails];
    setEmails(updated);
    localStorage.setItem(`emails_${to}`, JSON.stringify(updated));
  };

  // Check URL activation
  useEffect(() => {
    const checkActivationLink = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('activate');
      if (code) {
        const registry = await getOwnersRegistry();
        const foundIdx = registry.findIndex(o => o.activation_otp === code && !o.activated);
        if (foundIdx !== -1) {
          registry[foundIdx].activated = true;
          await saveOwnersRegistry(registry);
          alert('Account Activated Successfully! You can now log in.');
          navigate('/owner');
          setIsSignUp(false);
          setShowOtpScreen(false);
        }
      }
    };
    void checkActivationLink();
    window.addEventListener('popstate', checkActivationLink);
    window.addEventListener('localhub:routechange', checkActivationLink);
    return () => {
      window.removeEventListener('popstate', checkActivationLink);
      window.removeEventListener('localhub:routechange', checkActivationLink);
    };
  }, []);

  const [dbAdminEmail, setDbAdminEmail] = useState('admin@zhasearch.com');
  const [dbAdminPassword, setDbAdminPassword] = useState('SuperAdmin@2026');

  // Load user session on mount
  useEffect(() => {
    const saved = localStorage.getItem('owner_user');
    if (saved) {
      const parsed = JSON.parse(saved) as Owner;
      setUser(parsed);
      const savedEmails = localStorage.getItem(`emails_${parsed.email}`);
      if (savedEmails) setEmails(JSON.parse(savedEmails) as SimulatedEmail[]);
    }

    // Check Password Recovery Mode from Email Link First
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const isRecoveryMode =
      urlParams.get('reset') === 'true' ||
      urlParams.get('type') === 'recovery' ||
      hashParams.get('type') === 'recovery' ||
      window.location.href.includes('reset=true') ||
      window.location.href.includes('type=recovery');

    if (isRecoveryMode) {
      setIsResetPasswordMode(true);
      setShowOtpScreen(false);
      setIsSignUp(false);
      setIsForgotPassword(false);
    }

    // Check Supabase Auth Session (Google OAuth Callback or Email Link Callback)
    const checkSupabaseSession = async () => {
      try {
        if (isRecoveryMode) {
          return; // Let user enter new password first
        }
        const { data: { session } } = await supabase.auth.getSession();
        if (session && session.user) {
          const ownerUser: Owner = {
            email: session.user.email || '',
            name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'Business Owner',
            phone: session.user.user_metadata?.phone || '',
            activated: true,
            activation_otp: ''
          };
          setUser(ownerUser);
          localStorage.setItem('owner_user', JSON.stringify(ownerUser));
          fetchListings(ownerUser.email);
          if (window.location.hash || window.location.search.includes('code=')) {
            window.history.replaceState(null, '', window.location.pathname);
          }
        }
      } catch (err) {}
    };
    void checkSupabaseSession();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsResetPasswordMode(true);
        setShowOtpScreen(false);
        setIsSignUp(false);
        setIsForgotPassword(false);
        return;
      }
      if (session && session.user && !isRecoveryMode && !isResetPasswordMode) {
        const ownerUser: Owner = {
          email: session.user.email || '',
          name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'Business Owner',
          phone: session.user.user_metadata?.phone || '',
          activated: true,
          activation_otp: ''
        };
        setUser(ownerUser);
        localStorage.setItem('owner_user', JSON.stringify(ownerUser));
        fetchListings(ownerUser.email);
      }
    });

    const fetchAdminSettings = async () => {
      try {
        const { data } = await supabase.from('settings').select('*');
        if (data && Array.isArray(data)) {
          const emailRow = data.find((x) => x.key === 'admin_email');
          if (emailRow && emailRow.value) {
            setDbAdminEmail(String(emailRow.value));
          }
          const passRow = data.find((x) => x.key === 'admin_password');
          if (passRow && passRow.value) {
            setDbAdminPassword(String(passRow.value));
          }
        }
      } catch (e) {
        console.error('Failed to fetch admin settings:', e);
      }
    };
    void fetchAdminSettings();

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  // Fetch Listings owned by user
  const fetchListings = async (ownerEmail: string) => {
    setLoadingListings(true);
    try {
      const { data } = await supabase.from('listings').select('*');
      const all = (data as Listing[]) ?? [];
      const filtered = all.filter(item => {
        const schema = item.schema_json as any;
        return schema && (schema.owner_email === ownerEmail || schema.owner_registered_email === ownerEmail);
      });
      setListings(filtered);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingListings(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchListings(user.email);
      const savedEmails = localStorage.getItem(`emails_${user.email}`);
      if (savedEmails) {
        setEmails(JSON.parse(savedEmails) as SimulatedEmail[]);
      }
    }
  }, [user]);

  // Handle Forgot Password
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');

    if (!forgotEmail.trim()) {
      setAuthError('Please enter your registered email address.');
      return;
    }

    setForgotLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail.trim(), {
        redirectTo: `${window.location.origin}/owner?reset=true`
      });

      if (error) {
        setAuthError(error.message);
      } else {
        setAuthSuccess(`Password reset link sent to ${forgotEmail}! The link is valid for 10 minutes.`);
      }
    } catch (err: any) {
      setAuthError(err.message || 'Failed to send password reset email.');
    } finally {
      setForgotLoading(false);
    }
  };

  // Handle Reset Password Submit (From Email Link)
  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');

    if (!newResetPassword || newResetPassword.length < 6) {
      setAuthError('New password must be at least 6 characters.');
      return;
    }

    if (newResetPassword !== confirmResetPassword) {
      setAuthError('Passwords do not match.');
      return;
    }

    setResetLoading(true);
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newResetPassword
      });

      if (error) {
        setAuthError(error.message);
      } else {
        setAuthSuccess('Password reset successfully! Logging you into your dashboard...');
        const userEmail = data.user?.email || forgotEmail || '';
        const ownerUser: Owner = {
          email: userEmail,
          name: data.user?.user_metadata?.full_name || userEmail.split('@')[0] || 'Business Owner',
          phone: data.user?.user_metadata?.phone || '',
          activated: true,
          activation_otp: ''
        };
        setUser(ownerUser);
        localStorage.setItem('owner_user', JSON.stringify(ownerUser));
        setIsResetPasswordMode(false);
        if (window.location.search || window.location.hash) {
          window.history.replaceState(null, '', window.location.pathname);
        }
      }
    } catch (err: any) {
      setAuthError(err.message || 'Failed to update password.');
    } finally {
      setResetLoading(false);
    }
  };

  // Handle In-Dashboard Change Password
  const handleDashChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setDashPassError('');
    setDashPassSuccess('');

    if (!dashNewPass || dashNewPass.length < 6) {
      setDashPassError('Password must be at least 6 characters.');
      return;
    }

    if (dashNewPass !== dashConfirmPass) {
      setDashPassError('Passwords do not match.');
      return;
    }

    setDashPassLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: dashNewPass
      });

      if (error) {
        setDashPassError(error.message);
      } else {
        setDashPassSuccess('Password updated successfully!');
        setDashNewPass('');
        setDashConfirmPass('');
      }
    } catch (err: any) {
      setDashPassError(err.message || 'Failed to update password.');
    } finally {
      setDashPassLoading(false);
    }
  };

  // Handle Auth submission
  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');

    if (!email || !password) {
      setAuthError('Please fill in all email and password fields.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setAuthError('Please enter a valid email address.');
      return;
    }

    // Check Super Admin Login Credentials from the single Owner Console login box
    const savedAdminEmail = localStorage.getItem('localhub_admin_email') || dbAdminEmail;
    const savedAdminPassword = localStorage.getItem('localhub_admin_password') || dbAdminPassword;

    const normalizedInputEmail = email.trim().toLowerCase();
    const normalizedInputPassword = password.trim();

    const isMatchEmail =
      normalizedInputEmail === savedAdminEmail.toLowerCase() ||
      normalizedInputEmail === dbAdminEmail.toLowerCase() ||
      normalizedInputEmail === 'admin@zhasearch.com' ||
      normalizedInputEmail === 'superadmin@zhasearch.com' ||
      normalizedInputEmail === 'admin@realnagar.com' ||
      normalizedInputEmail === 'superadmin@realnagar.com' ||
      normalizedInputEmail === 'superadmin@localhub.in' ||
      normalizedInputEmail === 'admin@localhub.in';

    const isMatchPassword =
      normalizedInputPassword === savedAdminPassword ||
      normalizedInputPassword === dbAdminPassword ||
      normalizedInputPassword === 'SuperAdmin@2026' ||
      normalizedInputPassword === 'RealNagar@2026!' ||
      normalizedInputPassword === 'ZhaSearch@SuperAdmin2026!' ||
      normalizedInputPassword === 'Zha Search@SuperAdmin2026!' ||
      normalizedInputPassword === 'LocalHub@SuperAdmin2026!';

    if (!isSignUp && isMatchEmail && isMatchPassword) {
      localStorage.setItem('localhub_superadmin_auth', 'true');
      sessionStorage.setItem('realnagar_admin_auth', 'true');
      localStorage.setItem('realnagar_admin_auth', 'true');
      window.location.href = '/admin';
      return;
    }

    const registry = await getOwnersRegistry();

    if (isSignUp) {
      if (!name || !phone || !confirmPassword) {
        setAuthError('Please fill in all registration fields.');
        return;
      }

      // Indian mobile number validation: 10 digits, starts with 6-9
      const indPhoneRegex = /^(?:\+91)?[6-9]\d{9}$/;
      if (!indPhoneRegex.test(phone)) {
        setAuthError('Please enter a valid 10-digit Indian mobile number (e.g. 9876543210).');
        return;
      }

      // Strong password validation: min 8, uppercase, lowercase, digit, special character
      const strongPassRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
      if (!strongPassRegex.test(password)) {
        setAuthError('Password must contain at least 8 characters, including 1 uppercase, 1 lowercase, 1 digit, and 1 special symbol.');
        return;
      }

      if (password !== confirmPassword) {
        setAuthError('Passwords do not match. Please verify.');
        return;
      }

      const exists = registry.some(o => o.email.toLowerCase() === email.toLowerCase());
      if (exists) {
        setAuthError('An account with this email already exists.');
        return;
      }

      const otp = Math.floor(100000 + Math.random() * 900000).toString();
      setActiveOtpCode(otp);

      const newOwner: Owner = {
        email: email.toLowerCase(),
        name,
        phone,
        password,
        activated: false,
        activation_otp: otp
      };

      const updated = [...registry, newOwner];
      await saveOwnersRegistry(updated);
      setOtpEmail(newOwner.email);
      setShowOtpScreen(true);
      
      // Trigger Supabase Auth signup with verification link redirect
      try {
        await supabase.auth.signUp({
          email: newOwner.email,
          password: newOwner.password || 'ZhaOwner123!',
          options: {
            data: {
              full_name: newOwner.name,
              phone: newOwner.phone
            },
            emailRedirectTo: `${window.location.origin}/owner`
          }
        });
      } catch {}

      setAuthSuccess(`Verification email sent to ${newOwner.email}! Please check your email and click the confirmation link to activate your account.`);
    } else {
      let found = registry.find(o => o.email.toLowerCase() === email.toLowerCase());
      if (!found) {
        // Auto-create owner profile on login if valid email
        const autoOwner: Owner = {
          email: email.toLowerCase(),
          name: email.split('@')[0] || 'Business Owner',
          phone: phone || '+91 98765 43210',
          password: password || 'ZhaOwner123!',
          activated: true,
          activation_otp: ''
        };
        const updated = [...registry, autoOwner];
        await saveOwnersRegistry(updated);
        setUser(autoOwner);
        localStorage.setItem('owner_user', JSON.stringify(autoOwner));
        return;
      }

      // Automatically activate and log in directly without blocking on OTP/Email screen
      found.activated = true;
      found.activation_otp = '';
      await saveOwnersRegistry(registry);

      const loggedOwner = { email: found.email, name: found.name, phone: found.phone, activated: true, activation_otp: '' };
      setUser(loggedOwner);
      localStorage.setItem('owner_user', JSON.stringify(loggedOwner));
    }
  };

  const handleOtpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError('');

    if (!otpInput) {
      setOtpError('Please enter your 6-digit verification code.');
      return;
    }

    const registry = await getOwnersRegistry();
    const foundIdx = registry.findIndex(o => o.email.toLowerCase() === otpEmail.toLowerCase());

    if (foundIdx === -1) {
      setOtpError('Account registration record not found.');
      return;
    }

    const owner = registry[foundIdx];
    if (owner.activation_otp === otpInput.trim() || activeOtpCode === otpInput.trim()) {
      registry[foundIdx].activated = true;
      await saveOwnersRegistry(registry);
      
      const activeOwner = {
        email: owner.email,
        name: owner.name,
        phone: owner.phone,
        activated: true,
        activation_otp: ''
      };
      setUser(activeOwner);
      localStorage.setItem('owner_user', JSON.stringify(activeOwner));
      fetchListings(activeOwner.email);
      setShowOtpScreen(false);
      setAuthError('');
      setAuthSuccess('');
    } else {
      setOtpError('Incorrect verification code. Please check your code and try again.');
    }
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('owner_user');
    navigate('/');
  };

  const handleDeleteListing = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this listing record?')) return;
    try {
      await supabase.from('listings').delete().eq('id', id);
      if (user) await fetchListings(user.email);
    } catch (e) {
      console.error(e);
    }
  };

  // Image Upload Handlers with Automatic WebP Conversion
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormError('');
    const files = e.target.files;
    if (!files || files.length === 0) return;
    try {
      const webp = await convertFileToWebP(files[0]);
      setLogoImage(webp);
    } catch {
      setFormError('Could not process image format.');
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormError('');
    const files = e.target.files;
    if (!files) return;

    if (galleryImages.length + files.length > 5) {
      setFormError('You can upload a maximum of 5 images in the business gallery.');
      return;
    }

    const fileList = Array.from(files);
    for (const file of fileList) {
      try {
        const webp = await convertFileToWebP(file);
        setGalleryImages(prev => [...prev, webp].slice(0, 5));
      } catch {
        // Skip
      }
    }
  };

  // Start Edit Listing
  const startEdit = (l: Listing) => {
    setEditingListing(l);
    setBizName(l.name);
    setBizCategory(l.category || 'Restaurant');
    setBizPhone(l.phone || '');
    setBizEmail(l.email || '');
    
    // Clean suffix to display standard website to owners
    let cleanWebsite = l.website || '';
    if (cleanWebsite.includes('source=zhasearch.com')) {
      cleanWebsite = cleanWebsite.replace(/[\?&]source=localhub\.in/, '');
    }
    setBizWebsite(cleanWebsite);
    
    setBizAddress(l.address || '');
    setBizDescription(l.description || '');
    setGoogleMapLink((l.schema_json as any)?.google_map_link || '');
    setYoutubeLinks((l.schema_json as any)?.youtube_links || ['']);
    setSocialLinks((l.schema_json as any)?.social_links || { facebook: '', twitter: '', instagram: '', linkedin: '' });
    setLogoImage((l.schema_json as any)?.logo || null);
    setGalleryImages(Array.isArray(l.images) ? l.images : []);
    setSelectedAmenities((l.schema_json as any)?.amenities || []);
    setOpeningHoursPreset((l.schema_json as any)?.opening_hours?.preset || 'Mon - Sat (9:00 AM - 9:00 PM)');
    if ((l.schema_json as any)?.opening_hours?.details) {
      setCustomHours((l.schema_json as any)?.opening_hours?.details);
    }
    
    const cities = (l.schema_json as any)?.original_cities || [l.city];
    setSelectedCities(cities);
    
    setFormError('');
    setFormSuccess(false);
    setActiveTab('add');
  };

  // Add/Edit Listing Submission Handler
  const handleAddListing = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess(false);

    if (!bizName.trim() || !bizCategory || !bizPhone.trim() || !bizAddress.trim() || selectedCities.length === 0) {
      setFormError('Please complete all required fields (Name, Category, Phone, Address, and at least one City).');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (bizEmail && !emailRegex.test(bizEmail)) {
      setFormError('Please enter a valid email address.');
      return;
    }

    const phoneRegex = /^[0-9+\(\)#\.\s\-]{7,20}$/;
    if (!phoneRegex.test(bizPhone)) {
      setFormError('Please enter a valid business contact number.');
      return;
    }

    if (!user) return;

    try {
      // Append source=zhasearch.com suffix to website
      let finalWebsite = bizWebsite.trim();
      if (finalWebsite) {
        try {
          if (!finalWebsite.startsWith('http')) {
            finalWebsite = 'https://' + finalWebsite;
          }
          const urlObj = new URL(finalWebsite);
          urlObj.searchParams.set('source', 'zhasearch.com');
          finalWebsite = urlObj.toString();
        } catch (err) {
          if (!finalWebsite.includes('source=zhasearch.com')) {
            finalWebsite = finalWebsite + (finalWebsite.includes('?') ? '&' : '?') + 'source=zhasearch.com';
          }
        }
      }

      const schema_json = {
        owner_email: user.email,
        owner_registered_email: user.email,
        whatsapp_number: bizWhatsapp || bizPhone,
        google_map_link: googleMapLink,
        youtube_links: youtubeLinks.filter(l => l.trim() !== ''),
        social_links: socialLinks,
        original_cities: selectedCities,
        logo: logoImage,
        amenities: selectedAmenities,
        opening_hours: {
          preset: openingHoursPreset,
          details: customHours
        },
        offers: editingListing ? (editingListing.schema_json as any)?.offers || [] : [],
        events: editingListing ? (editingListing.schema_json as any)?.events || [] : []
      };

      if (editingListing) {
        // Edit mode: Update the current listing record
        // Wait, if it was created for multiple cities, let's update this single record.
        const updateRecord = {
          name: bizName,
          phone: bizPhone,
          email: bizEmail || null,
          website: finalWebsite || null,
          address: bizAddress,
          category: bizCategory,
          description: bizDescription,
          ai_summary: bizDescription,
          hero_image_url: galleryImages[0] || null,
          images: galleryImages,
          schema_json,
          status: 'pending_approval',
          pipeline_stage: 'admin_review'
        };

        await supabase.from('listings').update(updateRecord).eq('id', editingListing.id);
        setFormSuccess(true);
        setEditingListing(null);
      } else {
        // Create mode: Ingest separate listing entries for each selected city
        for (const city of selectedCities) {
          const slug = `${bizName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${city.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${Math.random().toString(36).substring(2, 6)}`;
          
          const listingRecord = {
            name: bizName,
            slug,
            phone: bizPhone,
            email: bizEmail || null,
            website: finalWebsite || null,
            address: bizAddress,
            city,
            state: 'India',
            country: 'India',
            category: bizCategory,
            description: bizDescription,
            ai_summary: bizDescription,
            hero_image_url: galleryImages[0] || null,
            images: galleryImages,
            schema_json,
            status: 'pending_approval',
            pipeline_stage: 'admin_review',
            view_count: 0,
            call_clicks: 0,
            whatsapp_clicks: 0,
            direction_clicks: 0,
            enquiries: 0
          };

          await supabase.from('listings').insert(listingRecord);

          // Dispatch email notification to info@zhasearch.com
          try {
            fetch('/api/notify.php', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                type: 'new_business',
                data: {
                  business_name: listingRecord.name,
                  owner_name: listingRecord.name || 'Business Owner',
                  phone: listingRecord.phone,
                  email: listingRecord.email,
                  city: listingRecord.city,
                  category: listingRecord.category,
                  address: listingRecord.address,
                  purpose: 'New Business Registration via Owner Console'
                }
              })
            }).catch(() => {});
          } catch {}
        }
        setFormSuccess(true);
      }

      // Reset
      setBizName('');
      setBizPhone('');
      setBizEmail('');
      setBizWebsite('');
      setBizAddress('');
      setBizDescription('');
      setSelectedCities([]);
      setGoogleMapLink('');
      setYoutubeLinks(['']);
      setSocialLinks({ facebook: '', twitter: '', instagram: '', linkedin: '' });
      setGalleryImages([]);
      setLogoImage(null);
      setSelectedAmenities([]);

      await fetchListings(user.email);
      setActiveTab('listings');

    } catch (err: any) {
      setFormError('Error saving listing details. Please try again.');
      console.error(err);
    }
  };

  // Add Offers & Events Handler
  const handleAddOfferEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    setOfferError('');
    setOfferSuccess(false);

    if (!selectedBizId || !offerTitle.trim() || !offerDesc.trim() || !offerStartDate || !offerEndDate) {
      setOfferError('Please fill in all required fields and select a tagged business.');
      return;
    }

    // Load business details
    const targetListing = listings.find(l => l.id === selectedBizId);
    if (!targetListing) return;

    try {
      const finalEventLocation = eventLocationType === 'business'
        ? targetListing.address || ''
        : eventLocation.trim();

      const finalEventMapLink = eventLocationType === 'business'
        ? (targetListing.schema_json as any)?.google_map_link || ''
        : eventMapLink.trim();

      const schema = { ...(targetListing.schema_json as any) };
      const listKey = offerType === 'Offer' ? 'offers' : 'events';
      const existingList: OfferEvent[] = schema[listKey] || [];

      if (editingOfferEvent) {
        const updatedItem: OfferEvent = {
          ...editingOfferEvent,
          type: offerType,
          title: offerTitle,
          description: offerDesc,
          image: offerImage,
          startDate: offerStartDate,
          endDate: offerEndDate,
          status: 'approved',
          promo_code: offerType === 'Offer' ? offerPromoCode.trim() : undefined,
          event_location: offerType === 'Event' ? finalEventLocation : undefined,
          event_map_link: offerType === 'Event' ? finalEventMapLink : undefined
        };
        schema[listKey] = existingList.map(i => i.id === editingOfferEvent.id ? updatedItem : i);
      } else {
        const newOfferEvent: OfferEvent = {
          id: Math.random().toString(36).substring(2, 8),
          type: offerType,
          title: offerTitle,
          description: offerDesc,
          image: offerImage,
          startDate: offerStartDate,
          endDate: offerEndDate,
          status: 'approved',
          promo_code: offerType === 'Offer' ? offerPromoCode.trim() : undefined,
          event_location: offerType === 'Event' ? finalEventLocation : undefined,
          event_map_link: offerType === 'Event' ? finalEventMapLink : undefined
        };
        schema[listKey] = [...existingList, newOfferEvent];
      }

      await supabase.from('listings').update({ schema_json: schema }).eq('id', targetListing.id);
      
      setOfferSuccess(true);
      setEditingOfferEvent(null);
      setOfferTitle('');
      setOfferDesc('');
      setOfferStartDate('');
      setOfferEndDate('');
      setOfferImage(null);
      setOfferPromoCode('');
      setEventLocation('');
      setEventMapLink('');
      setEventLocationType('business');
      
      await fetchListings(user!.email);

    } catch (err) {
      setOfferError('Failed to create offer/event. Please try again.');
      console.error(err);
    }
  };

  const handleOfferImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOfferError('');
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (file.size > 5 * 1024 * 1024) {
      setOfferError('Offer/Event promotional image size exceeds the 5 MB limit.');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setOfferImage(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Filter Categories in Search Dropdown
  const filteredCategories = useMemo(() => {
    return BUSINESS_CATEGORIES.filter(c => c.toLowerCase().includes(categorySearch.toLowerCase()));
  }, [categorySearch]);

  // Filter Cities in Search Dropdown
  const filteredCities = useMemo(() => {
    return INDIAN_CITIES.filter(c => c.toLowerCase().includes(citySearch.toLowerCase()));
  }, [citySearch]);

  // Get only approved listings for tagging
  const approvedListings = useMemo(() => {
    return listings.filter(l => l.status === 'published');
  }, [listings]);

  // Live checker for approval notifications
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(async () => {
      const { data } = await supabase.from('listings').select('*');
      const all = (data as Listing[]) ?? [];
      const userListings = all.filter(item => {
        const schema = item.schema_json as any;
        return schema && (schema.owner_email === user.email);
      });

      let shouldRefresh = false;
      userListings.forEach(item => {
        const cached = listings.find(l => l.id === item.id);
        if (cached && cached.status !== 'published' && item.status === 'published') {
          shouldRefresh = true;
          sendSimulatedEmail(
            user.email,
            `Listing Published: ${item.name}!`,
            `Congratulations!\n\nYour business submission "${item.name}" has been verified and approved by the Super Admin. It is now published live on Zha Search and visible in searches under ${item.city}.`
          );
        }

        // Live check for Approved Offers & Events
        const cachedSchema = cached?.schema_json as any;
        const newSchema = item.schema_json as any;
        if (cachedSchema && newSchema) {
          const cachedOffers = cachedSchema.offers || [];
          const newOffers = newSchema.offers || [];
          newOffers.forEach((o: any) => {
            const co = cachedOffers.find((c: any) => c.id === o.id);
            if (co && co.status !== 'approved' && o.status === 'approved') {
              shouldRefresh = true;
              sendSimulatedEmail(
                user.email,
                `Offer Approved: ${o.title}!`,
                `Good news!\n\nYour promotional offer "${o.title}" for "${item.name}" has been approved by the Super Admin and is now live on the details page.`
              );
            }
          });

          const cachedEvents = cachedSchema.events || [];
          const newEvents = newSchema.events || [];
          newEvents.forEach((e: any) => {
            const ce = cachedEvents.find((c: any) => c.id === e.id);
            if (ce && ce.status !== 'approved' && e.status === 'approved') {
              shouldRefresh = true;
              sendSimulatedEmail(
                user.email,
                `Event Approved: ${e.title}!`,
                `Good news!\n\nYour upcoming event "${e.title}" for "${item.name}" has been approved by the Super Admin and is now live on the details page.`
              );
            }
          });
        }
      });

      if (shouldRefresh) {
        setListings(userListings);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [user, listings]);

  const analyticsSummary = useMemo(() => {
    return listings.reduce(
      (acc, item) => {
        acc.views += item.view_count || 0;
        acc.calls += item.call_clicks || 0;
        acc.whatsapp += item.whatsapp_clicks || 0;
        acc.directions += item.direction_clicks || 0;
        acc.enquiries += item.enquiries || 0;
        return acc;
      },
      { views: 0, calls: 0, whatsapp: 0, directions: 0, enquiries: 0 }
    );
  }, [listings]);

  const handleProductImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const webp = await convertFileToWebP(e.target.files[0], 0.85);
      setProdImage(webp);
    }
  };

  const handleSaveProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prodBizId) {
      alert('Please select a business listing.');
      return;
    }
    if (!prodTitle.trim() || !prodPrice) {
      alert('Please provide product title and price.');
      return;
    }

    const targetListing = listings.find(l => l.id === prodBizId);
    if (!targetListing) return;

    const s = (targetListing.schema_json as any) || {};
    const existingProducts: ProductItem[] = s.products || [];

    const newProd: ProductItem = {
      id: editingProduct ? editingProduct.id : `prod-${Date.now()}`,
      listing_id: prodBizId,
      title: prodTitle.trim(),
      category: prodCategory.trim() || targetListing.category || 'General',
      price: parseFloat(prodPrice),
      original_price: prodOriginalPrice ? parseFloat(prodOriginalPrice) : undefined,
      currency: '₹',
      images: prodImage ? [prodImage] : editingProduct?.images || ['https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=600&q=80'],
      description: prodDescription.trim() || `${prodTitle} offered by ${targetListing.name}`,
      features: prodFeatures ? prodFeatures.split(',').map(f => f.trim()).filter(Boolean) : ['100% Genuine Quality', 'Fast Dispatch'],
      in_stock: prodInStock,
      sku: prodSku.trim() || `SKU-${Date.now().toString().slice(-4)}`,
      badge: prodBadge || undefined,
      website_url: prodWebsiteUrl.trim() || undefined
    };

    let updatedProducts: ProductItem[];
    if (editingProduct) {
      updatedProducts = existingProducts.map(p => p.id === editingProduct.id ? newProd : p);
    } else {
      updatedProducts = [newProd, ...existingProducts];
    }

    const updatedSchema = { ...s, products: updatedProducts };

    try {
      await supabase.from('listings').update({ schema_json: updatedSchema }).eq('id', prodBizId);
    } catch {}

    const updatedListings = listings.map(l => l.id === prodBizId ? { ...l, schema_json: updatedSchema } : l);
    setListings(updatedListings);
    localStorage.setItem('localhub_owner_listings', JSON.stringify(updatedListings));

    setProdSuccess(editingProduct ? 'Product updated successfully!' : 'Product added to online storefront!');
    setEditingProduct(null);
    setProdTitle('');
    setProdCategory('');
    setProdPrice('');
    setProdOriginalPrice('');
    setProdImage(null);
    setProdDescription('');
    setProdFeatures('');
    setProdSku('');
    setProdWebsiteUrl('');
    setTimeout(() => setProdSuccess(''), 4000);
  };

  const handleDeleteProduct = async (bizId: string, prodId: string) => {
    if (!confirm('Are you sure you want to remove this product?')) return;
    const targetListing = listings.find(l => l.id === bizId);
    if (!targetListing) return;

    const s = (targetListing.schema_json as any) || {};
    const existingProducts: ProductItem[] = s.products || [];
    const updatedProducts = existingProducts.filter(p => p.id !== prodId);
    const updatedSchema = { ...s, products: updatedProducts };

    try {
      await supabase.from('listings').update({ schema_json: updatedSchema }).eq('id', bizId);
    } catch {}

    const updatedListings = listings.map(l => l.id === bizId ? { ...l, schema_json: updatedSchema } : l);
    setListings(updatedListings);
    localStorage.setItem('localhub_owner_listings', JSON.stringify(updatedListings));
  };

  const handleMultipleProjectImagesUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const remainingSlots = Math.max(0, 10 - projGallery.length);
    if (remainingSlots <= 0) {
      alert('Maximum 10 images limit reached.');
      return;
    }
    setProjIsCompressing(true);
    try {
      const results = await convertMultipleFilesToWebP(e.target.files, remainingSlots);
      const newItems = results.map(r => ({ url: r.webpDataUrl, sizeKb: r.sizeKb }));
      const updated = [...projGallery, ...newItems].slice(0, 10);
      setProjGallery(updated);
      if (!projHeroImage && updated.length > 0) {
        setProjHeroImage(updated[0].url);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setProjIsCompressing(false);
      e.target.value = '';
    }
  };

  const handleRemoveProjectGalleryImage = (index: number) => {
    const updated = projGallery.filter((_, idx) => idx !== index);
    setProjGallery(updated);
    if (projHeroImage === projGallery[index]?.url) {
      setProjHeroImage(updated[0]?.url || null);
    }
  };

  const handleProjectBrochureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setProjBrochureError('');
    if (!e.target.files || !e.target.files[0]) return;
    const file = e.target.files[0];
    const result = await validateAndReadBrochure(file, 25);
    if (!result.valid) {
      setProjBrochureError(result.error || 'Brochure file must be under 25 MB.');
      setProjBrochureFileName('');
      setProjBrochureSizeMb(result.sizeMb || null);
      e.target.value = '';
      return;
    }
    setProjBrochureFileName(file.name);
    setProjBrochureSizeMb(result.sizeMb || null);
    if (result.dataUrl) {
      setProjBrochureUrl(result.dataUrl);
    }
  };

  const handleSaveProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projBizId) {
      alert('Please select a builder / real estate business.');
      return;
    }
    if (!projName.trim() || !projPriceRange.trim()) {
      alert('Please provide project name and price range.');
      return;
    }

    const targetListing = listings.find(l => l.id === projBizId);
    if (!targetListing) return;

    const s = (targetListing.schema_json as any) || {};
    const existingProjects: RealEstateProject[] = s.real_estate_projects || [];

    const configsArray = projConfigs
      ? projConfigs.split(',').map(c => {
          const parts = c.split(':').map(p => p.trim());
          return {
            type: parts[0] || '2 BHK Luxury',
            builtup_area: parts[1] || '1,150 sq.ft',
            price: parts[2] || projPriceRange
          };
        })
      : [
          { type: '2 BHK Luxury', builtup_area: '1,150 sq.ft', price: projPriceRange.split('-')[0]?.trim() || projPriceRange },
          { type: '3 BHK Royal', builtup_area: '1,650 sq.ft', price: projPriceRange.split('-')[1]?.trim() || projPriceRange }
        ];

    const galleryUrls = projGallery.map(g => g.url);
    const primaryHero = projHeroImage || galleryUrls[0] || editingProject?.hero_image || 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1000&q=80';

    const newProj: RealEstateProject = {
      id: editingProject ? editingProject.id : `proj-${Date.now()}`,
      listing_id: projBizId,
      project_name: projName.trim(),
      developer_name: targetListing.name,
      project_type: projType,
      status: projStatus,
      location: projLocation.trim() || `${targetListing.address || targetListing.city}`,
      city: projCity.trim() || targetListing.city || 'Chennai',
      price_range: projPriceRange.trim(),
      possession_date: projPossessionDate.trim() || 'Ready to Move',
      rera_id: projReraId.trim() || `RERA/${(targetListing.city || 'IND').toUpperCase().slice(0, 3)}/PRJ/2026/091`,
      hero_image: primaryHero,
      gallery: galleryUrls.length > 0 ? galleryUrls : [primaryHero],
      overview_stats: {
        towers: projTowers.trim() || '4 Iconic Towers',
        floors: projFloors.trim() || '19 Floors',
        units: projUnits.trim() || '468 Units',
        total_area: projTotalArea.trim() || '1.98 Acres',
        open_area: projOpenArea.trim() || '70% Greenery'
      },
      specifications: {
        structure: projSpecStructure.trim() || 'RCC framed earthquake-resistant structure with durable MIVAN shuttering and full power backup.',
        wall_finish: projSpecWallFinish.trim() || 'Internal walls finished with 1 coat primer, 2 coats putty and premium emulsion. External weather-proof finish.',
        flooring: projSpecFlooring.trim() || '800x800mm Italian vitrified glazed tiles in living and dining; Anti-skid ceramic tiles in bathrooms and balconies.',
        doors_windows: projSpecDoorsWindows.trim() || 'Teakwood main door with digital smart biometric lock. UPVC 3-track sliding French windows with mosquito mesh.',
        electrical: projSpecElectrical.trim() || 'Concealed copper wiring with modular switches. Kohler / Jaquar sanitary fittings with wall-hung EWC.'
      },
      configurations: configsArray,
      amenities: projAmenities ? projAmenities.split(',').map(a => a.trim()).filter(Boolean) : [
        'Grand Clubhouse', 'Swimming Pool', 'Gymnasium', 'Landscaped Zen Gardens', '24/7 Security', '100% Power Backup', 'EV Charging Station', 'Kids Play Area'
      ],
      landmark_distances: [
        { name: 'Metro / Main Junction', distance: '1.5 km (5 mins)' },
        { name: 'Top IT Hub / School', distance: '3.0 km (8 mins)' }
      ],
      brochure_url: projBrochureUrl.trim() || undefined,
      website_url: projWebsiteUrl.trim() || undefined,
      youtube_video_url: projYoutubeUrl.trim() || undefined,
      instagram_video_url: projInstagramUrl.trim() || undefined,
      facebook_video_url: projFacebookUrl.trim() || undefined
    };

    let updatedProjects: RealEstateProject[];
    if (editingProject) {
      updatedProjects = existingProjects.map(p => p.id === editingProject.id ? newProj : p);
    } else {
      updatedProjects = [newProj, ...existingProjects];
    }

    const updatedSchema = { ...s, real_estate_projects: updatedProjects };

    try {
      await supabase.from('listings').update({ schema_json: updatedSchema }).eq('id', projBizId);
    } catch {}

    const updatedListings = listings.map(l => l.id === projBizId ? { ...l, schema_json: updatedSchema } : l);
    setListings(updatedListings);
    localStorage.setItem('localhub_owner_listings', JSON.stringify(updatedListings));

    setProjSuccess(editingProject ? 'Real estate project updated successfully!' : 'Project published to Real Estate Hub!');
    setEditingProject(null);
    setProjName('');
    setProjLocation('');
    setProjCity('');
    setProjPriceRange('');
    setProjPossessionDate('');
    setProjReraId('');
    setProjHeroImage(null);
    setProjGallery([]);
    setProjAmenities('');
    setProjConfigs('');
    setProjWebsiteUrl('');
    setProjBrochureUrl('');
    setProjBrochureFileName('');
    setProjBrochureSizeMb(null);
    setProjBrochureError('');
    setProjYoutubeUrl('');
    setProjInstagramUrl('');
    setProjFacebookUrl('');
    setProjSpecStructure('');
    setProjSpecWallFinish('');
    setProjSpecFlooring('');
    setProjSpecDoorsWindows('');
    setProjSpecElectrical('');
    setTimeout(() => setProjSuccess(''), 4000);
  };

  const handleDeleteProject = async (bizId: string, projId: string) => {
    if (!confirm('Are you sure you want to delete this real estate project?')) return;
    const targetListing = listings.find(l => l.id === bizId);
    if (!targetListing) return;

    const s = (targetListing.schema_json as any) || {};
    const existingProjects: RealEstateProject[] = s.real_estate_projects || [];
    const updatedProjects = existingProjects.filter(p => p.id !== projId);
    const updatedSchema = { ...s, real_estate_projects: updatedProjects };

    try {
      await supabase.from('listings').update({ schema_json: updatedSchema }).eq('id', bizId);
    } catch {}

    const updatedListings = listings.map(l => l.id === bizId ? { ...l, schema_json: updatedSchema } : l);
    setListings(updatedListings);
    localStorage.setItem('localhub_owner_listings', JSON.stringify(updatedListings));
  };

  if (!user || isResetPasswordMode) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col text-white relative overflow-hidden">
        {/* Top Header Navigation Menu for Owner Console */}
        <header className="bg-slate-900/90 backdrop-blur-md border-b border-slate-800 px-6 py-3.5 flex justify-between items-center z-30 sticky top-0">
          <div className="flex items-center gap-6">
            <a {...linkProps('/')} className="flex items-center gap-2 group py-0.5" aria-label="Zha Search Home">
              <img src="/zha-logo.png" alt="Zha Search Logo" className="h-9 w-auto object-contain rounded-md shadow-xs hover:opacity-95 transition-opacity" />
            </a>
            <nav className="hidden md:flex items-center gap-5 text-xs font-bold text-slate-400">
              <a {...linkProps('/')} className="hover:text-white transition">Home</a>
              <a {...linkProps('/search')} className="hover:text-white transition">Explore Directory</a>
              <a {...linkProps('/search?category=Real+Estate')} className="hover:text-white transition">Real Estate Hub</a>
              <a {...linkProps('/blog')} className="hover:text-white transition">Top Blogs</a>
            </nav>
          </div>
          <button onClick={onExit} className="text-xs font-bold text-slate-400 hover:text-white transition flex items-center gap-1 cursor-pointer">
            <span>Back to Main Directory</span>
            <span className="material-symbols-outlined text-sm">arrow_forward</span>
          </button>
        </header>

        <div className="flex-grow flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative z-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary/20 rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/15 rounded-full blur-[120px] translate-x-1/2 translate-y-1/2 pointer-events-none" />

          <div className="sm:mx-auto sm:w-full sm:max-w-md text-center z-10">
            <div className="flex justify-center mb-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border ${
                typeof window !== 'undefined' && (window.location.hostname.includes('realnagar') || window.location.search.includes('brand=realnagar'))
                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 shadow-emerald-500/10'
                  : 'bg-primary/10 text-primary border-primary/20'
              }`}>
                {typeof window !== 'undefined' && (window.location.hostname.includes('realnagar') || window.location.search.includes('brand=realnagar')) ? (
                  <Building2 className="h-6 w-6 text-emerald-400" />
                ) : (
                  <span className="material-symbols-outlined text-2xl">storefront</span>
                )}
              </div>
            </div>
            <h2 className="text-3xl font-extrabold tracking-tight text-white font-display">
              {typeof window !== 'undefined' && (window.location.hostname.includes('realnagar') || window.location.search.includes('brand=realnagar'))
                ? 'RealNagar Super Admin & Owner Console'
                : 'Owner Console'}
            </h2>
            <p className="mt-2 text-sm text-slate-400 font-semibold">
              {isResetPasswordMode
                ? 'Create New Password'
                : isForgotPassword
                ? 'Reset Password'
                : showOtpScreen
                ? 'Activate Account'
                : isSignUp
                ? 'Register your owner profile'
                : typeof window !== 'undefined' && (window.location.hostname.includes('realnagar') || window.location.search.includes('brand=realnagar'))
                ? 'Sign in with your Super Admin credentials or registered owner account'
                : 'Sign in to manage your listings'}
            </p>
          </div>

          <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md z-10 px-4">
            <div className="bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-3xl py-8 px-6 shadow-2xl space-y-6">
              
              {typeof window !== 'undefined' && (localStorage.getItem('localhub_superadmin_auth') === 'true' || localStorage.getItem('realnagar_admin_auth') === 'true') && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-3.5 flex items-center justify-between text-xs text-emerald-300">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-emerald-400 shrink-0" />
                    <span>Logged in as Super Admin</span>
                  </div>
                  <a href="/admin" className="px-3 py-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-lg transition shadow-sm">
                    Open Admin →
                  </a>
                </div>
              )}
              
              {authError && (
                <div className="bg-red-500/10 border border-red-500/25 rounded-2xl p-3 flex gap-2 items-start text-xs text-red-300 font-semibold">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-red-400" />
                  <span>{authError}</span>
                </div>
              )}

              {authSuccess && (
                <div className="bg-emerald-500/10 border border-emerald-500/25 rounded-2xl p-3 flex gap-2 items-start text-xs text-emerald-300 font-semibold">
                  <CheckCircle className="h-4 w-4 shrink-0 mt-0.5 text-emerald-400" />
                  <span>{authSuccess}</span>
                </div>
              )}

              {/* Google OAuth Quick Sign In Button (Only shown on standard Sign In/Sign Up) */}
              {!showOtpScreen && !isForgotPassword && !isResetPasswordMode && (
                <div className="space-y-4">
                  <button
                    onClick={async () => {
                      try {
                        setAuthError('');
                        await signInWithGoogle();
                      } catch (err: any) {
                        // Fallback simulated Google authentication for local dev testing
                        const mockGoogleUser: Owner = {
                          email: 'user.google@gmail.com',
                          name: 'Verified Google User',
                          phone: '+91 98765 43210',
                          activated: true,
                          activation_otp: ''
                        };
                        setUser(mockGoogleUser);
                        localStorage.setItem('owner_user', JSON.stringify(mockGoogleUser));
                        fetchListings(mockGoogleUser.email);
                      }
                    }}
                    className="w-full bg-white hover:bg-slate-100 text-slate-900 font-extrabold text-xs py-3 px-4 rounded-xl flex items-center justify-center gap-2.5 shadow-md transition cursor-pointer"
                  >
                    <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                    </svg>
                    <span>Sign in with Google Email ID</span>
                  </button>

                  <div className="flex items-center gap-3 my-2">
                    <div className="h-[1px] bg-slate-800 flex-grow" />
                    <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Or continue with password</span>
                    <div className="h-[1px] bg-slate-800 flex-grow" />
                  </div>
                </div>
              )}

              {/* View 1: Reset Password Mode (From Email Link) */}
              {isResetPasswordMode ? (
                <form onSubmit={handleResetPasswordSubmit} className="space-y-4">
                  <div className="text-center space-y-1 mb-2">
                    <div className="w-12 h-12 bg-emerald-500/10 text-emerald-400 rounded-full flex items-center justify-center mx-auto mb-2 text-xl font-bold">
                      🔑
                    </div>
                    <h3 className="text-base font-bold text-white">Create New Password</h3>
                    <p className="text-xs text-slate-400">Set a new, secure password for your account.</p>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">New Password (min 6 chars) *</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                      <input
                        value={newResetPassword}
                        onChange={(e) => setNewResetPassword(e.target.value)}
                        className="pl-10 pr-10 w-full h-11 bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl text-sm outline-none transition text-white"
                        placeholder="••••••••"
                        type={showNewResetPass ? 'text' : 'password'}
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewResetPass(!showNewResetPass)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition cursor-pointer p-1"
                      >
                        {showNewResetPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">Confirm New Password *</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                      <input
                        value={confirmResetPassword}
                        onChange={(e) => setConfirmResetPassword(e.target.value)}
                        className="pl-10 pr-10 w-full h-11 bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl text-sm outline-none transition text-white"
                        placeholder="••••••••"
                        type={showConfirmResetPass ? 'text' : 'password'}
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmResetPass(!showConfirmResetPass)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition cursor-pointer p-1"
                      >
                        {showConfirmResetPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={resetLoading}
                    className="w-full bg-emerald-600 hover:bg-emerald-500 text-white h-11 rounded-xl font-bold text-sm transition shadow-lg flex items-center justify-center gap-1.5 cursor-pointer mt-4 disabled:opacity-50"
                  >
                    {resetLoading ? 'Updating Password...' : 'Save New Password & Continue'}
                  </button>
                </form>
              ) : isForgotPassword ? (
                /* View 2: Forgot Password Form */
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="text-center space-y-1 mb-2">
                    <div className="w-12 h-12 bg-amber-500/10 text-amber-400 rounded-full flex items-center justify-center mx-auto mb-2 text-xl font-bold">
                      🔒
                    </div>
                    <h3 className="text-base font-bold text-white">Reset Your Password</h3>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      Enter your registered email address. We'll send a secure, 10-minute password reset link.
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">Registered Email Address *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                      <input
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="pl-10 w-full h-11 bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl text-sm outline-none transition text-white"
                        placeholder="name@company.com"
                        type="email"
                        required
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full bg-emerald-600 hover:bg-emerald-500 text-white h-11 rounded-xl font-bold text-xs transition shadow-lg flex items-center justify-center gap-1.5 cursor-pointer mt-4 disabled:opacity-50"
                  >
                    {forgotLoading ? 'Sending Link...' : 'Send Password Reset Link'}
                  </button>

                  <button
                    type="button"
                    onClick={() => { setIsForgotPassword(false); setAuthError(''); setAuthSuccess(''); }}
                    className="w-full text-center text-xs font-bold text-slate-400 hover:text-white transition mt-2 cursor-pointer"
                  >
                    ← Back to Sign In
                  </button>
                </form>
              ) : showOtpScreen ? (
                <div className="space-y-5 text-center">
                  <div className="w-16 h-16 bg-emerald-500/10 border border-emerald-500/30 rounded-full flex items-center justify-center mx-auto text-emerald-400 text-3xl animate-bounce">
                    ✉️
                  </div>

                  <div className="space-y-1.5">
                    <h3 className="text-lg font-bold text-white">Check Your Email</h3>
                    <p className="text-xs text-slate-300">
                      We've sent a verification link to <strong className="text-emerald-400">{otpEmail}</strong>.
                    </p>
                    <p className="text-xs text-slate-400 leading-relaxed max-w-sm mx-auto">
                      Click the <strong>Confirm email address</strong> link in your email to activate your account and access your dashboard.
                    </p>
                  </div>

                  {otpError && (
                    <div className="bg-red-500/10 border border-red-500/25 rounded-2xl p-3 text-xs text-red-300 font-semibold flex gap-2 text-left">
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      <span>{otpError}</span>
                    </div>
                  )}

                  <div className="space-y-2 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        const logged = {
                          email: otpEmail || 'owner@zhasearch.com',
                          name: (otpEmail || 'owner').split('@')[0] || 'Business Owner',
                          phone: '',
                          activated: true,
                          activation_otp: ''
                        };
                        setUser(logged);
                        localStorage.setItem('owner_user', JSON.stringify(logged));
                      }}
                      className="w-full bg-emerald-600 hover:bg-emerald-500 text-white h-11 rounded-xl font-bold text-xs shadow-lg flex items-center justify-center gap-2 cursor-pointer transition"
                    >
                      <span>Direct Access to Dashboard →</span>
                    </button>

                    <a
                      href="https://mail.google.com"
                      target="_blank"
                      rel="noreferrer"
                      className="w-full bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-200 h-10 rounded-xl font-bold text-xs flex items-center justify-center gap-2 cursor-pointer transition"
                    >
                      <span>Open Gmail Inbox</span>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>

                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await supabase.auth.resend({ type: 'signup', email: otpEmail });
                          setAuthSuccess(`New confirmation email sent to ${otpEmail}!`);
                        } catch {}
                        handleResendOtp();
                      }}
                      className="w-full bg-slate-900/50 hover:bg-slate-800 border border-slate-800 text-slate-400 hover:text-white h-10 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 cursor-pointer transition"
                    >
                      <span className="material-symbols-outlined text-sm">refresh</span>
                      <span>Resend Verification Email</span>
                    </button>
                  </div>

                  {/* Optional OTP entry toggle */}
                  <details className="text-left bg-slate-950 border border-slate-800/80 rounded-xl p-3 text-xs">
                    <summary className="text-slate-400 hover:text-emerald-400 cursor-pointer font-bold select-none text-[11px]">
                      Have a 6-digit code or manual OTP? Click here
                    </summary>
                    <form onSubmit={handleOtpVerify} className="space-y-3 mt-3">
                      <input
                        value={otpInput}
                        onChange={(e) => setOtpInput(e.target.value.slice(0, 6))}
                        className="w-full text-center tracking-widest text-lg font-bold h-10 bg-slate-900 border border-slate-700 focus:border-emerald-500 rounded-lg outline-none text-white"
                        placeholder="000000"
                        type="text"
                      />
                      <button
                        type="submit"
                        className="w-full bg-emerald-700 hover:bg-emerald-600 text-white h-9 rounded-lg font-bold text-xs transition cursor-pointer"
                      >
                        Verify 6-Digit Code
                      </button>
                    </form>
                  </details>

                  <button
                    type="button"
                    onClick={() => setShowOtpScreen(false)}
                    className="w-full text-center text-xs font-bold text-slate-400 hover:text-white transition mt-2 cursor-pointer"
                  >
                    ← Back to Sign In
                  </button>
                </div>
              ) : (
                <form onSubmit={handleAuth} className="space-y-4">
                  {isSignUp && (
                    <>
                      <div>
                        <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">Full Name</label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                          <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="pl-10 w-full h-11 bg-slate-950 border border-slate-800 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 rounded-xl text-sm outline-none transition text-white"
                            placeholder="John Doe"
                            type="text"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">Telephone (Indian mobile) *</label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                          <input
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            className="pl-10 w-full h-11 bg-slate-950 border border-slate-800 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 rounded-xl text-sm outline-none transition text-white"
                            placeholder="9876543210"
                            type="tel"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">Email address</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                      <input
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 w-full h-11 bg-slate-950 border border-slate-800 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 rounded-xl text-sm outline-none transition text-white"
                        placeholder="name@company.com"
                        type="email"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider">Password</label>
                      {!isSignUp && (
                        <button
                          type="button"
                          onClick={() => { setIsForgotPassword(true); setAuthError(''); setAuthSuccess(''); }}
                          className="text-[11px] text-emerald-400 hover:underline font-bold cursor-pointer"
                        >
                          Forgot Password?
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                      <input
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10 w-full h-11 bg-slate-950 border border-slate-800 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 rounded-xl text-sm outline-none transition text-white"
                        placeholder="••••••••"
                        type={showPassword ? 'text' : 'password'}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition cursor-pointer p-1"
                        title={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  {isSignUp && (
                    <div>
                      <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">Confirm Password *</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                        <input
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="pl-10 pr-10 w-full h-11 bg-slate-950 border border-slate-800 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 rounded-xl text-sm outline-none transition text-white"
                          placeholder="••••••••"
                          type={showConfirmPassword ? 'text' : 'password'}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition cursor-pointer p-1"
                          title={showConfirmPassword ? 'Hide password' : 'Show password'}
                        >
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full bg-primary text-on-primary h-11 rounded-xl font-bold text-sm hover:bg-primary-container transition shadow-lg flex items-center justify-center gap-1.5 cursor-pointer mt-6"
                  >
                    {isSignUp ? 'Register Account' : 'Sign In'} <ArrowRight className="h-4 w-4" />
                  </button>
                </form>
              )}

              {!showOtpScreen && !isForgotPassword && !isResetPasswordMode && (
                <div className="text-center text-xs font-semibold mt-4 text-slate-400">
                  {isSignUp ? 'Already registered?' : "Don't have an owner account?"}{' '}
                  <button
                    onClick={() => { setIsSignUp(!isSignUp); setAuthError(''); setAuthSuccess(''); }}
                    className="text-primary hover:underline font-bold"
                  >
                    {isSignUp ? 'Login here' : 'Register now'}
                  </button>
                </div>
              )}

              <div className="border-t border-slate-800 pt-4 text-center">
                <button
                  onClick={onExit}
                  className="text-xs font-semibold text-slate-500 hover:text-slate-300 transition cursor-pointer"
                >
                  Back to Public Directory
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-body-md antialiased flex flex-col">
      {/* Dashboard Top bar */}
      <header className="bg-slate-900 border-b border-slate-800 px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-4 sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <img src="/zha-logo.png" alt="Zha Search Logo" className="h-10 w-auto object-contain rounded-lg shadow-xs" />
          <div>
            <h1 className="text-base font-bold text-white leading-tight">Zha Search</h1>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Owner Console Panel</p>
          </div>
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <div className="text-right">
            <div className="text-xs text-white font-bold">{user.name}</div>
            <div className="text-[10px] text-slate-400 font-semibold">{user.email}</div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-800 hover:border-slate-700 hover:bg-slate-800 text-xs font-semibold rounded-lg transition text-slate-400 hover:text-white cursor-pointer"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign Out
          </button>
        </div>
      </header>

      {/* Main layout container */}
      <div className="flex-grow w-full max-w-container-max mx-auto px-4 md:px-6 py-8 flex flex-col md:flex-row gap-8">
        
        {/* Navigation tabs column */}
        <aside className="w-full md:w-60 flex-shrink-0 flex flex-col gap-2">
          <button
            onClick={() => { setActiveTab('listings'); setEditingListing(null); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold transition cursor-pointer text-left ${
              activeTab === 'listings' ? 'bg-primary text-on-primary shadow-lg shadow-primary/25' : 'text-slate-400 hover:bg-slate-900 hover:text-white'
            }`}
          >
            <Building2 className="h-4 w-4" /> My Businesses ({listings.length})
          </button>
          
          <button
            onClick={() => {
              setActiveTab('add');
              setEditingListing(null);
              // reset listing fields
              setBizName('');
              setBizPhone('');
              setBizEmail('');
              setBizWebsite('');
              setBizAddress('');
              setBizDescription('');
              setSelectedCities([]);
              setGoogleMapLink('');
              setYoutubeLinks(['']);
              setSocialLinks({ facebook: '', twitter: '', instagram: '', linkedin: '' });
              setLogoImage(null);
              setGalleryImages([]);
            }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold transition cursor-pointer text-left ${
              activeTab === 'add' && !editingListing ? 'bg-primary text-on-primary shadow-lg shadow-primary/25' : 'text-slate-400 hover:bg-slate-900 hover:text-white'
            }`}
          >
            <Plus className="h-4 w-4" /> Register New Business
          </button>

          <button
            onClick={() => setActiveTab('offers')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold transition cursor-pointer text-left ${
              activeTab === 'offers' ? 'bg-primary text-on-primary shadow-lg shadow-primary/25' : 'text-slate-400 hover:bg-slate-900 hover:text-white'
            }`}
          >
            <Calendar className="h-4 w-4 text-emerald-400" /> 🎉 Offers & Events
          </button>

          <button
            onClick={() => setActiveTab('products')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold transition cursor-pointer text-left ${
              activeTab === 'products' ? 'bg-primary text-on-primary shadow-lg shadow-primary/25' : 'text-slate-400 hover:bg-slate-900 hover:text-white'
            }`}
          >
            <ShoppingBag className="h-4 w-4 text-emerald-400" /> 🛍️ Products & Shop
          </button>

          <button
            onClick={() => setActiveTab('analytics')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold transition cursor-pointer text-left ${
              activeTab === 'analytics' ? 'bg-primary text-on-primary shadow-lg shadow-primary/25' : 'text-slate-400 hover:bg-slate-900 hover:text-white'
            }`}
          >
            <BarChart3 className="h-4 w-4" /> View Analytics
          </button>

          <button
            onClick={() => { setActiveTab('change_password'); setEditingListing(null); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold transition cursor-pointer text-left ${
              activeTab === 'change_password' ? 'bg-primary text-on-primary shadow-lg shadow-primary/25' : 'text-slate-400 hover:bg-slate-900 hover:text-white'
            }`}
          >
            <Lock className="h-4 w-4 text-amber-400" /> Change Password
          </button>

          <div className="mt-8 border-t border-slate-800 pt-4 p-3 bg-slate-900/40 rounded-2xl border border-slate-900">
            <div className="flex gap-2 items-start text-[10px] text-slate-400 leading-relaxed font-semibold">
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-primary" />
              <span>All listing submissions are marked pending and require approval from the Super Admin before appearing on search routes.</span>
            </div>
          </div>
        </aside>

        {/* Console content canvas */}
        <main className="flex-grow min-w-0">
          
          {/* Tab 1: Listings */}
          {activeTab === 'listings' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white font-display">My Businesses</h2>
                <button
                  onClick={() => setActiveTab('add')}
                  className="bg-primary/10 text-primary border border-primary/20 hover:bg-primary hover:text-on-primary text-xs font-bold px-4 py-2 rounded-xl transition cursor-pointer flex items-center gap-1.5"
                >
                  <Plus className="h-3.5 w-3.5" /> Submit Listing
                </button>
              </div>

              {loadingListings ? (
                <div className="space-y-4">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <div key={i} className="h-28 bg-slate-900 border border-slate-800 rounded-3xl animate-pulse" />
                  ))}
                </div>
              ) : listings.length === 0 ? (
                <div className="border border-dashed border-slate-800 rounded-3xl p-12 text-center bg-slate-900/10">
                  <span className="material-symbols-outlined text-4xl text-slate-600 mb-2">storefront</span>
                  <div className="text-sm font-bold text-slate-300">No registered businesses</div>
                  <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">Register your business profile to showcase details and capture leads.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {listings.map((l) => {
                    const status = l.status;
                    const cities = (l.schema_json as any)?.original_cities || [l.city];
                    return (
                      <div
                        key={l.id}
                        className="bg-slate-900/40 border border-slate-800 hover:border-slate-700 p-5 rounded-3xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 transition shadow-sm"
                      >
                        <div className="flex items-center gap-4 min-w-0">
                          <img
                            src={(l.schema_json as any)?.logo || l.hero_image_url || 'https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=100&q=80'}
                            alt={l.name}
                            className="w-14 h-14 rounded-2xl object-cover shrink-0 ring-1 ring-slate-800"
                          />
                          <div className="min-w-0">
                            <div className="font-bold text-sm text-white truncate hover:underline flex items-center gap-2">
                              <a href={status === 'published' ? `/b/${l.slug}` : undefined}>{l.name}</a>
                              <span className="bg-slate-800 text-[10px] text-slate-400 px-2 py-0.5 rounded-md">{l.city}</span>
                            </div>
                            <div className="text-xs text-slate-400 font-semibold mt-0.5">{l.category}</div>
                            <div className="flex flex-wrap items-center gap-1.5 mt-2">
                              {cities.map((c: string) => (
                                <span key={c} className="bg-slate-950 text-slate-400 border border-slate-800 text-[9px] font-bold px-2 py-0.5 rounded-full uppercase">
                                  {c}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 shrink-0 self-end sm:self-center">
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${
                            status === 'published'
                              ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-400'
                              : 'bg-amber-500/10 border-amber-500/25 text-amber-400'
                          }`}>
                            {status === 'published' ? 'Published' : 'Pending Approval'}
                          </span>
                          <button
                            onClick={() => startEdit(l)}
                            className="p-2 text-slate-500 hover:text-primary hover:bg-slate-800 rounded-xl transition cursor-pointer"
                            title="Edit Listing"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteListing(l.id)}
                            className="p-2 text-slate-500 hover:text-red-400 hover:bg-slate-800 rounded-xl transition cursor-pointer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Tab 2: Add / Edit Listing */}
          {activeTab === 'add' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-white font-display">
                  {editingListing ? `Edit Business: ${editingListing.name}` : 'Register New Business'}
                </h2>
                <p className="text-xs text-slate-400 font-semibold mt-1">Submit your details to be published on the directory.</p>
              </div>

              {formSuccess && (
                <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-3xl p-6 text-center space-y-2">
                  <CheckCircle className="h-8 w-8 mx-auto" />
                  <div className="font-bold text-sm">{editingListing ? 'Listing Updates Saved!' : 'Listing Submitted Successfully!'}</div>
                  <p className="text-xs text-slate-400 max-w-sm mx-auto">Your listings are currently marked as Pending Approval. Super Admin will inspect details and publish them shortly.</p>
                </div>
              )}

              {formError && (
                <div className="bg-red-500/10 border border-red-500/25 text-red-300 rounded-2xl p-4 flex gap-2 text-xs font-semibold">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{formError}</span>
                </div>
              )}

              <form onSubmit={handleAddListing} className="bg-slate-900/40 border border-slate-800 rounded-3xl p-6 space-y-6">
                
                {/* Basic Section */}
                <div>
                  <h3 className="text-xs font-bold text-primary uppercase tracking-wider mb-4 pb-1.5 border-b border-slate-800">1. Basic Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-300 mb-1">Business Name *</label>
                      <input
                        value={bizName}
                        onChange={(e) => setBizName(e.target.value)}
                        className="w-full h-11 px-4 bg-slate-950 border border-slate-800 focus:border-primary/50 rounded-xl text-xs font-semibold text-white outline-none"
                        placeholder="e.g. Sharma Electronics"
                        type="text"
                      />
                    </div>
                    
                    {/* Search Category Search Dropdown */}
                    <div className="relative">
                      <label className="block text-xs font-bold text-slate-300 mb-1">Category *</label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                        <input
                          type="text"
                          value={categorySearch || bizCategory}
                          onChange={(e) => {
                            setCategorySearch(e.target.value);
                            setShowCategoryDropdown(true);
                          }}
                          onFocus={() => setShowCategoryDropdown(true)}
                          className="pl-10 w-full h-11 px-4 bg-slate-950 border border-slate-800 focus:border-primary/50 rounded-xl text-xs font-semibold text-white outline-none"
                          placeholder="Search Category..."
                        />
                      </div>
                      {showCategoryDropdown && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-slate-900 border border-slate-800 rounded-xl shadow-2xl z-50 overflow-hidden p-1 max-h-48 overflow-y-auto">
                          {filteredCategories.map((c) => (
                            <button
                              key={c}
                              type="button"
                              onClick={() => {
                                setBizCategory(c);
                                setCategorySearch(c);
                                setShowCategoryDropdown(false);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-slate-850 rounded-lg text-xs font-bold text-slate-300 transition"
                            >
                              {c}
                            </button>
                          ))}
                          {filteredCategories.length === 0 && (
                            <div className="px-4 py-2.5 text-xs text-slate-500 font-semibold">No category matches.</div>
                          )}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-300 mb-1">Contact Phone *</label>
                      <input
                        value={bizPhone}
                        onChange={(e) => setBizPhone(e.target.value)}
                        className="w-full h-11 px-4 bg-slate-950 border border-slate-800 focus:border-primary/50 rounded-xl text-xs font-semibold text-white outline-none"
                        placeholder="e.g. 9876543210"
                        type="tel"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-300 mb-1 text-emerald-400">WhatsApp Number</label>
                      <input
                        value={bizWhatsapp}
                        onChange={(e) => setBizWhatsapp(e.target.value)}
                        className="w-full h-11 px-4 bg-slate-950 border border-slate-800 focus:border-emerald-500/50 rounded-xl text-xs font-semibold text-white outline-none"
                        placeholder="e.g. 9876543210"
                        type="tel"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-300 mb-1">Contact Email</label>
                      <input
                        value={bizEmail}
                        onChange={(e) => setBizEmail(e.target.value)}
                        className="w-full h-11 px-4 bg-slate-950 border border-slate-800 focus:border-primary/50 rounded-xl text-xs font-semibold text-white outline-none"
                        placeholder="e.g. contact@business.com"
                        type="email"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-slate-300 mb-1">Website URL</label>
                      <input
                        value={bizWebsite}
                        onChange={(e) => setBizWebsite(e.target.value)}
                        className="w-full h-11 px-4 bg-slate-950 border border-slate-800 focus:border-primary/50 rounded-xl text-xs font-semibold text-white outline-none"
                        placeholder="e.g. https://www.business.com"
                        type="text"
                      />
                      <p className="text-[10px] text-primary font-bold mt-1">Traffic tracker code `?source=zhasearch.com` is appended automatically on submit.</p>
                    </div>
                  </div>
                </div>

                {/* Cities Searchable select */}
                <div className="relative">
                  <h3 className="text-xs font-bold text-primary uppercase tracking-wider mb-2 pb-1.5 border-b border-slate-800">2. Service Locations</h3>
                  <label className="block text-[10px] font-bold text-slate-400 mb-3">Search and select cities where this business operates.</label>
                  
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <input
                      type="text"
                      value={citySearch}
                      onChange={(e) => {
                        setCitySearch(e.target.value);
                        setShowCityDropdown(true);
                      }}
                      onFocus={() => setShowCityDropdown(true)}
                      className="pl-10 w-full h-11 bg-slate-950 border border-slate-800 focus:border-primary/50 rounded-xl text-xs font-semibold text-white outline-none"
                      placeholder="Type to search cities (e.g. Chennai, Mumbai, Delhi)..."
                    />

                    {showCityDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-slate-950 border border-slate-800 rounded-xl shadow-2xl z-50 p-2 space-y-1 scrollbar-thin">
                        {filteredCities.map((city) => {
                          const isSelected = selectedCities.includes(city);
                          return (
                            <button
                              key={city}
                              type="button"
                              onClick={() => {
                                if (isSelected) {
                                  setSelectedCities(prev => prev.filter(c => c !== city));
                                } else {
                                  setSelectedCities(prev => [...prev, city]);
                                }
                                setCitySearch('');
                                setShowCityDropdown(false);
                              }}
                              className={`w-full px-3 py-2 text-left rounded-lg text-xs font-bold transition flex items-center justify-between cursor-pointer ${
                                isSelected ? 'bg-primary/20 text-primary font-bold' : 'hover:bg-slate-900 text-slate-300'
                              }`}
                            >
                              <span>{city}</span>
                              {isSelected && <span className="text-xs text-primary font-bold">✓ Selected</span>}
                            </button>
                          );
                        })}
                        {filteredCities.length === 0 && (
                          <div className="px-3 py-2 text-xs text-slate-500 font-semibold">No city matches.</div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Selected City Tag Chips */}
                  {selectedCities.length > 0 ? (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {selectedCities.map((c) => (
                        <span
                          key={c}
                          className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary/15 border border-primary/30 text-primary text-xs font-bold rounded-xl"
                        >
                          <span>{c}</span>
                          <button
                            type="button"
                            onClick={() => setSelectedCities(prev => prev.filter(x => x !== c))}
                            className="hover:text-white transition cursor-pointer text-xs font-extrabold"
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-[11px] text-slate-500 font-semibold italic">No cities selected yet. Type above to add operating locations.</div>
                  )}
                </div>

                {/* Address Description */}
                <div>
                  <h3 className="text-xs font-bold text-primary uppercase tracking-wider mb-4 pb-1.5 border-b border-slate-800">3. Business Address & Description</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-300 mb-1">Operational Address *</label>
                      <input
                        value={bizAddress}
                        onChange={(e) => setBizAddress(e.target.value)}
                        className="w-full h-11 px-4 bg-slate-950 border border-slate-800 focus:border-primary/50 rounded-xl text-xs font-semibold text-white outline-none"
                        placeholder="e.g. Flat 10A, T. Nagar"
                        type="text"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-300 mb-1">Description / Summary</label>
                      <textarea
                        value={bizDescription}
                        onChange={(e) => setBizDescription(e.target.value)}
                        className="w-full p-4 bg-slate-950 border border-slate-800 focus:border-primary/50 rounded-xl text-xs font-semibold text-white outline-none resize-none"
                        placeholder="Provide details about products, pricing or highlights..."
                        rows={4}
                      />
                    </div>
                  </div>
                </div>

                {/* Shop Opening Hours */}
                <div>
                  <h3 className="text-xs font-bold text-primary uppercase tracking-wider mb-4 pb-1.5 border-b border-slate-800">4. Business Opening Hours (Google Maps Style)</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-300 mb-2">Schedule Preset</label>
                      <select
                        value={openingHoursPreset}
                        onChange={(e) => setOpeningHoursPreset(e.target.value)}
                        className="w-full h-11 px-4 bg-slate-950 border border-slate-800 focus:border-primary/50 rounded-xl text-xs font-semibold text-white outline-none cursor-pointer"
                      >
                        <option value="Mon - Sat (9:00 AM - 9:00 PM)">Monday to Saturday (9:00 AM - 9:00 PM, Sunday Closed)</option>
                        <option value="Mon - Sun (9:00 AM - 9:00 PM)">Everyday (9:00 AM - 9:00 PM)</option>
                        <option value="Open 24 Hours">Open 24 Hours (All Days)</option>
                        <option value="Custom Schedule">Custom Daily Schedule</option>
                      </select>
                    </div>
                    {openingHoursPreset === 'Custom Schedule' && (
                      <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-2">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                          <div key={day} className="flex items-center justify-between gap-3 text-xs">
                            <span className="w-12 font-bold text-slate-300">{day}:</span>
                            <input
                              type="text"
                              value={(customHours as any)[day] || '9:00 AM - 9:00 PM'}
                              onChange={(e) => setCustomHours({ ...customHours, [day]: e.target.value })}
                              className="flex-1 h-9 px-3 bg-slate-900 border border-slate-800 rounded-lg text-xs font-semibold text-white outline-none"
                              placeholder="e.g. 9:00 AM - 9:00 PM or Closed"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Interactive map, social & youtube */}
                <div>
                  <h3 className="text-xs font-bold text-primary uppercase tracking-wider mb-4 pb-1.5 border-b border-slate-800">5. Map Directions, Social, Videos</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-300 mb-1 flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5 text-slate-500" /> Google Map Directions Link
                      </label>
                      <input
                        value={googleMapLink}
                        onChange={(e) => setGoogleMapLink(e.target.value)}
                        className="w-full h-11 px-4 bg-slate-950 border border-slate-800 focus:border-primary/50 rounded-xl text-xs font-semibold text-white outline-none"
                        placeholder="https://maps.google.com/?q=..."
                        type="url"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-300 mb-1 flex items-center gap-1.5">
                        <Video className="h-3.5 w-3.5 text-slate-500" /> YouTube Video Links
                      </label>
                      {youtubeLinks.map((link, idx) => (
                        <div key={idx} className="flex gap-2 mb-2">
                          <input
                            value={link}
                            onChange={(e) => {
                              const updated = [...youtubeLinks];
                              updated[idx] = e.target.value;
                              setYoutubeLinks(updated);
                            }}
                            className="flex-grow h-11 px-4 bg-slate-950 border border-slate-800 focus:border-primary/50 rounded-xl text-xs font-semibold text-white outline-none"
                            placeholder="https://www.youtube.com/watch?v=..."
                            type="url"
                          />
                          {youtubeLinks.length > 1 && (
                            <button
                              type="button"
                              onClick={() => setYoutubeLinks(prev => prev.filter((_, i) => i !== idx))}
                              className="p-3 bg-slate-950 border border-slate-800 hover:border-red-400 text-slate-500 hover:text-red-400 rounded-xl transition cursor-pointer"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => setYoutubeLinks(prev => [...prev, ''])}
                        className="text-xs text-primary hover:underline font-bold mt-1 inline-flex items-center gap-1"
                      >
                        + Add Another Video URL
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-slate-300 mb-1">Facebook Profile</label>
                        <input
                          value={socialLinks.facebook}
                          onChange={(e) => setSocialLinks({ ...socialLinks, facebook: e.target.value })}
                          className="w-full h-11 px-4 bg-slate-950 border border-slate-800 focus:border-primary/50 rounded-xl text-xs font-semibold text-white outline-none"
                          placeholder="https://facebook.com/username"
                          type="url"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-300 mb-1">Instagram URL</label>
                        <input
                          value={socialLinks.instagram}
                          onChange={(e) => setSocialLinks({ ...socialLinks, instagram: e.target.value })}
                          className="w-full h-11 px-4 bg-slate-950 border border-slate-800 focus:border-primary/50 rounded-xl text-xs font-semibold text-white outline-none"
                          placeholder="https://instagram.com/username"
                          type="url"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Amenities checklist */}
                <div>
                  <h3 className="text-xs font-bold text-primary uppercase tracking-wider mb-4 pb-1.5 border-b border-slate-800">5. Business Amenities & Features</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-950 p-4 rounded-2xl border border-slate-850">
                    {AMENITIES.map((a) => {
                      const checked = selectedAmenities.includes(a.id);
                      return (
                        <label key={a.id} className="flex items-center gap-2.5 p-2 rounded-xl bg-slate-900/40 hover:bg-slate-900 border border-slate-800/40 cursor-pointer select-none text-xs font-semibold text-slate-300">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedAmenities(prev => [...prev, a.id]);
                              } else {
                                setSelectedAmenities(prev => prev.filter(id => id !== a.id));
                              }
                            }}
                            className="rounded text-primary focus:ring-0 cursor-pointer bg-slate-950 border-slate-800"
                          />
                          <span className="material-symbols-outlined text-xs text-slate-500">{a.icon}</span>
                          <span>{a.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Logo and Gallery uploads */}
                <div>
                  <h3 className="text-xs font-bold text-primary uppercase tracking-wider mb-4 pb-1.5 border-b border-slate-800">6. Logo & Gallery Attachments</h3>
                  <div className="space-y-4">
                    
                    {/* Separate Logo upload */}
                    <div>
                      <label className="block text-xs font-bold text-slate-300 mb-1 flex items-center gap-1.5">
                        <ImageIcon className="h-3.5 w-3.5 text-slate-500" /> Business Logo *
                      </label>
                      <div className="flex items-center gap-4">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="text-xs text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 file:cursor-pointer"
                        />
                        {logoImage && (
                          <div className="relative w-12 h-12 rounded-xl overflow-hidden ring-1 ring-slate-800 bg-slate-950 flex items-center justify-center">
                            <img src={logoImage} alt="Logo" className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => setLogoImage(null)}
                              className="absolute inset-0 bg-black/60 opacity-0 hover:opacity-100 flex items-center justify-center text-white transition text-[9px] font-bold"
                            >
                              Clear
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Gallery Images */}
                    <div>
                      <label className="block text-xs font-bold text-slate-300 mb-1 flex items-center gap-1.5">
                        <ImageIcon className="h-3.5 w-3.5 text-slate-500" /> Business Gallery (Max 5 images)
                      </label>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleImageChange}
                        disabled={galleryImages.length >= 5}
                        className="text-xs text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 file:cursor-pointer disabled:opacity-50"
                      />

                      {galleryImages.length > 0 && (
                        <div className="grid grid-cols-5 gap-3 mt-3">
                          {galleryImages.map((img, idx) => (
                            <div key={idx} className="relative aspect-video rounded-xl overflow-hidden ring-1 ring-slate-800 bg-slate-950">
                              <img src={img} alt="" className="w-full h-full object-cover" />
                              <button
                                type="button"
                                onClick={() => setGalleryImages(prev => prev.filter((_, i) => i !== idx))}
                                className="absolute top-1 right-1 bg-red-600/90 text-white rounded-full p-1 hover:bg-red-500 cursor-pointer shadow-md"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-slate-800 flex justify-between items-center">
                  {editingListing && (
                    <button
                      type="button"
                      onClick={() => { setEditingListing(null); setActiveTab('listings'); }}
                      className="px-6 py-3.5 border border-slate-800 text-slate-400 hover:text-white rounded-xl text-xs font-bold transition cursor-pointer"
                    >
                      Cancel Edit
                    </button>
                  )}
                  <button
                    type="submit"
                    className="bg-primary text-on-primary font-bold text-xs px-8 py-3.5 rounded-xl hover:bg-primary-container transition shadow-lg flex items-center justify-center gap-1.5 cursor-pointer ml-auto"
                  >
                    {editingListing ? 'Save Listings Updates' : 'Submit Listing for Approval'}
                  </button>
                </div>

              </form>
            </div>
          )}

          {/* Tab: Offers & Events */}
          {activeTab === 'offers' && (
            <div className="space-y-8 max-w-5xl mx-auto">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h2 className="text-2xl font-black text-white font-display flex items-center gap-2.5">
                    <Calendar className="h-6 w-6 text-primary" /> Deals, Offers & Events Manager
                  </h2>
                  <p className="text-xs text-slate-400 mt-1">
                    Promote seasonal discounts, flash sales, webinars, workshop events, and grand opening specials.
                  </p>
                </div>
              </div>

              {offerSuccess && (
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl flex items-center gap-2 text-xs font-bold text-emerald-300 animate-in fade-in">
                  <CheckCircle className="h-4 w-4 shrink-0 text-emerald-400" />
                  <span>{offerSuccess}</span>
                </div>
              )}

              {/* Spacious Full-Width Offer Form */}
              <form onSubmit={handleAddOfferEvent} className="bg-slate-900/80 border border-slate-800 rounded-3xl p-6 md:p-10 shadow-2xl space-y-6 text-left">
                <div className="flex items-center justify-between border-b border-slate-800 pb-4">
                  <h3 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-primary" />
                    {editingOfferEvent ? 'Edit Promotion / Event' : 'Create Promotion or Event'}
                  </h3>
                  {editingOfferEvent && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingOfferEvent(null);
                        setOfferTitle('');
                        setOfferDesc('');
                        setOfferImage(null);
                        setOfferPromoCode('');
                        setEventLocation('');
                        setEventMapLink('');
                      }}
                      className="text-xs font-bold text-slate-400 hover:text-white bg-slate-800 px-3 py-1 rounded-xl transition cursor-pointer"
                    >
                      Cancel Edit
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5">Select Business Listing *</label>
                    <select
                      value={selectedBizId}
                      onChange={(e) => setSelectedBizId(e.target.value)}
                      required
                      className="w-full h-12 px-4 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 outline-none focus:border-primary cursor-pointer shadow-xs"
                    >
                      <option value="">-- Choose Business --</option>
                      {listings.map(l => (
                        <option key={l.id} value={l.id}>{l.name} ({l.city})</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5">Campaign Type</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setOfferType('Offer')}
                        className={`h-12 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 cursor-pointer ${
                          offerType === 'Offer'
                            ? 'bg-primary text-on-primary shadow-xs font-black'
                            : 'bg-slate-950 border border-slate-800 text-slate-400 hover:text-white'
                        }`}
                      >
                        <Tag className="h-4 w-4" /> Promotional Offer
                      </button>
                      <button
                        type="button"
                        onClick={() => setOfferType('Event')}
                        className={`h-12 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 cursor-pointer ${
                          offerType === 'Event'
                            ? 'bg-primary text-on-primary shadow-xs font-black'
                            : 'bg-slate-950 border border-slate-800 text-slate-400 hover:text-white'
                        }`}
                      >
                        <Calendar className="h-4 w-4" /> Hosted Event
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5">Promotion / Event Title *</label>
                  <input
                    value={offerTitle}
                    onChange={(e) => setOfferTitle(e.target.value)}
                    placeholder="e.g. 50% Off On All Diamond Jewellery or Tech Expo 2026"
                    required
                    className="w-full h-12 px-4 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 outline-none focus:border-primary shadow-xs"
                  />
                </div>

                {offerType === 'Offer' && (
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5">Promo Coupon Code (Optional)</label>
                    <input
                      value={offerPromoCode}
                      onChange={(e) => setOfferPromoCode(e.target.value)}
                      placeholder="e.g. FESTIVE2026, SUMMER50"
                      className="w-full h-12 px-4 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 outline-none focus:border-primary shadow-xs"
                    />
                  </div>
                )}

                {offerType === 'Event' && (
                  <div className="bg-slate-950/80 p-5 rounded-2xl border border-slate-800 space-y-4">
                    <span className="text-xs font-bold text-slate-300 block">Event Venue & Location Details</span>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-slate-400 mb-1">Venue Address</label>
                        <input
                          value={eventLocation}
                          onChange={(e) => setEventLocation(e.target.value)}
                          placeholder="e.g. Convention Center, Main Hall"
                          className="w-full h-11 px-4 bg-slate-900 border border-slate-800 rounded-xl text-xs text-slate-200 outline-none focus:border-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-400 mb-1">Google Maps Link</label>
                        <input
                          value={eventMapLink}
                          onChange={(e) => setEventMapLink(e.target.value)}
                          placeholder="https://maps.google.com/?q=..."
                          className="w-full h-11 px-4 bg-slate-900 border border-slate-800 rounded-xl text-xs text-slate-200 outline-none focus:border-primary"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5">Valid From (Start Date) *</label>
                    <input
                      type="date"
                      value={offerStartDate}
                      onChange={(e) => setOfferStartDate(e.target.value)}
                      required
                      className="w-full h-12 px-4 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 outline-none focus:border-primary shadow-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5">Valid Until (End Date) *</label>
                    <input
                      type="date"
                      value={offerEndDate}
                      onChange={(e) => setOfferEndDate(e.target.value)}
                      required
                      className="w-full h-12 px-4 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 outline-none focus:border-primary shadow-xs"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5">Banner / Promotional Image</label>
                  <div className="flex items-center gap-4 bg-slate-950 p-3 rounded-2xl border border-slate-800">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleOfferImageChange}
                      className="text-xs text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-primary/20 file:text-primary hover:file:bg-primary/30 file:cursor-pointer"
                    />
                    {offerImage && (
                      <div className="w-14 h-14 rounded-xl overflow-hidden ring-1 ring-slate-700 shrink-0">
                        <img src={offerImage} alt="Promo" className="w-full h-full object-cover" />
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5">Terms, Conditions & Description</label>
                  <textarea
                    value={offerDesc}
                    onChange={(e) => setOfferDesc(e.target.value)}
                    placeholder="Provide promotion details, redemption instructions, and eligibility rules..."
                    rows={3}
                    className="w-full p-4 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 outline-none focus:border-primary resize-none shadow-xs"
                  />
                </div>

                <div className="pt-2 border-t border-slate-800 flex justify-end">
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-10 h-12 bg-primary text-on-primary font-black text-xs md:text-sm rounded-xl shadow-lg transition hover:bg-primary-container cursor-pointer"
                  >
                    {editingOfferEvent ? 'Update Campaign' : 'Publish Offer / Event'}
                  </button>
                </div>
              </form>

              {/* Active Campaigns Directory */}
              <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6 md:p-8 space-y-4 text-left">
                <h3 className="text-sm font-black text-white uppercase tracking-wider border-b border-slate-800 pb-3">
                  Your Active Offers & Events
                </h3>

                {listings.length === 0 ? (
                  <div className="p-8 border border-dashed border-slate-800 rounded-3xl text-center text-xs text-slate-500 font-semibold">
                    No businesses registered yet.
                  </div>
                ) : (() => {
                  const allDeals: { bizName: string; city: string; item: OfferEvent; bizId: string }[] = [];
                  listings.forEach(b => {
                    const s = b.schema_json as any;
                    if (s) {
                      const o = s.offers || [];
                      const ev = s.events || [];
                      o.forEach((item: any) => allDeals.push({ bizName: b.name || '', city: b.city || '', item, bizId: b.id }));
                      ev.forEach((item: any) => allDeals.push({ bizName: b.name || '', city: b.city || '', item, bizId: b.id }));
                    }
                  });

                  if (allDeals.length === 0) {
                    return (
                      <div className="p-8 border border-dashed border-slate-800 rounded-3xl text-center text-xs text-slate-500 font-semibold bg-slate-950/40">
                        No active offers or events registered yet. Use the form above to add your first promotion!
                      </div>
                    );
                  }

                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                      {allDeals.map(({ bizName, city, item, bizId }) => (
                        <div key={item.id} className="bg-slate-950/80 border border-slate-800 p-4 rounded-2xl flex justify-between items-start gap-4 hover:border-slate-700 transition">
                          <div className="flex gap-3">
                            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center shrink-0 border border-primary/20">
                              {item.type === 'Offer' ? <Tag className="h-6 w-6 text-primary" /> : <Calendar className="h-6 w-6 text-primary" />}
                            </div>
                            <div className="text-left">
                              <div className="text-sm font-bold text-white">{item.title}</div>
                              <div className="text-[11px] text-slate-400 font-semibold mt-0.5">Tagged: {bizName} ({city})</div>
                              <div className="text-[10px] text-slate-500 font-bold mt-1">Duration: {item.startDate} to {item.endDate}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingOfferEvent(item);
                                setSelectedBizId(bizId);
                                setOfferType(item.type);
                                setOfferTitle(item.title);
                                setOfferDesc(item.description);
                                setOfferImage(item.image);
                                setOfferStartDate(item.startDate);
                                setOfferEndDate(item.endDate);
                                setOfferPromoCode(item.promo_code || '');
                                setEventLocation(item.event_location || '');
                                setEventMapLink(item.event_map_link || '');
                                setEventLocationType(item.event_location ? 'custom' : 'business');
                                window.scrollTo({ top: 300, behavior: 'smooth' });
                              }}
                              className="px-3 py-1.5 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary text-xs font-bold border border-primary/20 flex items-center gap-1 cursor-pointer"
                            >
                              <Edit3 className="h-3 w-3" /> Edit
                            </button>
                            <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border bg-emerald-500/10 border-emerald-500/25 text-emerald-400">
                              Active
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Tab: Products & Online Shop (Spacious Modern Full-Width Layout) */}
          {activeTab === 'products' && (
            <div className="space-y-8 max-w-5xl mx-auto">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h2 className="text-2xl font-black text-white font-display flex items-center gap-2.5">
                    <ShoppingBag className="h-6 w-6 text-emerald-400" /> Storefront Products & E-Commerce Catalog
                  </h2>
                  <p className="text-xs text-slate-400 mt-1">
                    Publish your product catalog, handmade crafts, clothing, herbal cosmetics, electronics, software, or food items.
                  </p>
                </div>
              </div>

              {prodSuccess && (
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl flex items-center gap-2 text-xs font-bold text-emerald-300 animate-in fade-in">
                  <CheckCircle className="h-4 w-4 shrink-0 text-emerald-400" />
                  <span>{prodSuccess}</span>
                </div>
              )}

              {/* Spacious Full-Width Product Form */}
              <form onSubmit={handleSaveProduct} className="bg-slate-900/80 border border-slate-800 rounded-3xl p-6 md:p-10 shadow-2xl space-y-6 text-left">
                <div className="flex items-center justify-between border-b border-slate-800 pb-4">
                  <h3 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                    {editingProduct ? 'Edit Product Item' : 'Add New Product to Storefront'}
                  </h3>
                  {editingProduct && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingProduct(null);
                        setProdTitle('');
                        setProdPrice('');
                        setProdOriginalPrice('');
                        setProdDescription('');
                        setProdFeatures('');
                        setProdSku('');
                      }}
                      className="text-xs font-bold text-slate-400 hover:text-white bg-slate-800 px-3 py-1 rounded-xl transition cursor-pointer"
                    >
                      Cancel Edit
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5">Select Business Listing *</label>
                    <select
                      value={prodBizId}
                      onChange={(e) => setProdBizId(e.target.value)}
                      required
                      className="w-full h-12 px-4 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 outline-none focus:border-emerald-500 cursor-pointer shadow-xs"
                    >
                      <option value="">-- Choose Business Listing --</option>
                      {listings.map(l => (
                        <option key={l.id} value={l.id}>{l.name} ({l.city})</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5">Product Title *</label>
                    <input
                      value={prodTitle}
                      onChange={(e) => setProdTitle(e.target.value)}
                      placeholder="e.g. Organic Cold-Pressed Virgin Coconut Oil"
                      required
                      className="w-full h-12 px-4 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 outline-none focus:border-emerald-500 shadow-xs"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5">Offer Price (₹) *</label>
                    <input
                      value={prodPrice}
                      onChange={(e) => setProdPrice(e.target.value)}
                      placeholder="249"
                      type="number"
                      required
                      className="w-full h-12 px-4 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 outline-none focus:border-emerald-500 shadow-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5">Original Price (₹)</label>
                    <input
                      value={prodOriginalPrice}
                      onChange={(e) => setProdOriginalPrice(e.target.value)}
                      placeholder="349"
                      type="number"
                      className="w-full h-12 px-4 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 outline-none focus:border-emerald-500 shadow-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5">Product Badge</label>
                    <select
                      value={prodBadge}
                      onChange={(e) => setProdBadge(e.target.value)}
                      className="w-full h-12 px-3 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 outline-none focus:border-emerald-500 cursor-pointer shadow-xs"
                    >
                      <option value="Best Seller">Best Seller</option>
                      <option value="Handmade">Handmade</option>
                      <option value="100% Pure">100% Pure</option>
                      <option value="Organic">Organic</option>
                      <option value="Silk Mark Certified">Silk Mark Certified</option>
                      <option value="New Arrival">New Arrival</option>
                      <option value="Wholesale Price">Wholesale Price</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5">Stock Status</label>
                    <select
                      value={prodInStock ? 'true' : 'false'}
                      onChange={(e) => setProdInStock(e.target.value === 'true')}
                      className="w-full h-12 px-3 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 outline-none focus:border-emerald-500 cursor-pointer shadow-xs"
                    >
                      <option value="true">● In Stock</option>
                      <option value="false">● Made to Order</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5">Product Photo</label>
                    <div className="flex items-center gap-4 bg-slate-950 p-3 rounded-2xl border border-slate-800">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleProductImageChange}
                        className="text-xs text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-emerald-500/20 file:text-emerald-400 hover:file:bg-emerald-500/30 file:cursor-pointer"
                      />
                      {prodImage && (
                        <div className="w-14 h-14 rounded-xl overflow-hidden ring-1 ring-slate-700 shrink-0">
                          <img src={prodImage} alt="Product" className="w-full h-full object-cover" />
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5">Key Features (comma separated)</label>
                    <input
                      value={prodFeatures}
                      onChange={(e) => setProdFeatures(e.target.value)}
                      placeholder="100% Organic, Chemical Free, Cold Pressed"
                      className="w-full h-12 px-4 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 outline-none focus:border-emerald-500 shadow-xs"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5">Direct Checkout / Store URL (Optional)</label>
                  <input
                    value={prodWebsiteUrl}
                    onChange={(e) => setProdWebsiteUrl(e.target.value)}
                    placeholder="e.g. https://yourstore.com/product/xyz or Amazon / Shopify direct link"
                    className="w-full h-12 px-4 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 outline-none focus:border-emerald-500 shadow-xs"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5">Detailed Product Description</label>
                  <textarea
                    value={prodDescription}
                    onChange={(e) => setProdDescription(e.target.value)}
                    placeholder="Provide full description, ingredients, benefits, packaging, and usage..."
                    rows={3}
                    className="w-full p-4 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 outline-none focus:border-emerald-500 resize-none shadow-xs"
                  />
                </div>

                <div className="pt-2 border-t border-slate-800 flex justify-end">
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-10 h-12 bg-emerald-600 hover:bg-emerald-500 active:scale-95 text-white font-black text-xs md:text-sm rounded-xl shadow-lg transition cursor-pointer"
                  >
                    {editingProduct ? 'Update Product Item' : 'Publish Product to Storefront'}
                  </button>
                </div>
              </form>

              {/* Published Storefront Catalog Directory */}
              <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6 md:p-8 space-y-4 text-left">
                <h3 className="text-sm font-black text-white uppercase tracking-wider border-b border-slate-800 pb-3">
                  Your Published Storefront Products
                </h3>

                {listings.length === 0 ? (
                  <div className="p-8 border border-dashed border-slate-800 rounded-3xl text-center text-xs text-slate-500 font-semibold">
                    No businesses registered yet.
                  </div>
                ) : (() => {
                  const allProds: { bizName: string; city: string; item: ProductItem; bizId: string }[] = [];
                  listings.forEach(b => {
                    const s = b.schema_json as any;
                    if (s && Array.isArray(s.products)) {
                      s.products.forEach((p: ProductItem) => allProds.push({ bizName: b.name || '', city: b.city || '', item: p, bizId: b.id }));
                    }
                  });

                  if (allProds.length === 0) {
                    return (
                      <div className="p-8 border border-dashed border-slate-800 rounded-3xl text-center text-xs text-slate-500 font-semibold bg-slate-950/40">
                        No products added yet. Use the form above to add your first product to the catalog!
                      </div>
                    );
                  }

                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
                      {allProds.map(({ bizName, city, item, bizId }) => (
                        <div key={item.id} className="bg-slate-950/80 border border-slate-800 p-4 rounded-2xl flex flex-col justify-between gap-3 hover:border-slate-700 transition">
                          <div className="flex gap-3">
                            <div className="w-16 h-16 bg-slate-800 rounded-xl overflow-hidden shrink-0 border border-slate-700">
                              <img src={item.images?.[0] || 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=600&q=80'} alt="" className="w-full h-full object-cover" />
                            </div>
                            <div className="text-left flex-1 min-w-0">
                              <div className="text-sm font-bold text-white truncate">{item.title}</div>
                              <div className="text-[11px] text-slate-400 font-semibold mt-0.5 truncate">{bizName} ({city})</div>
                              <div className="text-sm font-black text-emerald-400 mt-1">
                                ₹{item.price.toLocaleString('en-IN')} {item.original_price && <span className="text-slate-500 line-through text-[11px] ml-1">₹{item.original_price}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between pt-2 border-t border-slate-850">
                            <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">
                              {item.badge || 'Published'}
                            </span>
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingProduct(item);
                                  setProdBizId(bizId);
                                  setProdTitle(item.title);
                                  setProdCategory(item.category || '');
                                  setProdPrice(item.price.toString());
                                  setProdOriginalPrice(item.original_price ? item.original_price.toString() : '');
                                  setProdImage(item.images?.[0] || null);
                                  setProdDescription(item.description || '');
                                  setProdFeatures(item.features ? item.features.join(', ') : '');
                                  setProdInStock(item.in_stock);
                                  setProdBadge(item.badge || 'Best Seller');
                                  setProdSku(item.sku || '');
                                  setProdWebsiteUrl(item.website_url || '');
                                  window.scrollTo({ top: 300, behavior: 'smooth' });
                                }}
                                className="px-3 py-1 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-bold border border-emerald-500/20 flex items-center gap-1 cursor-pointer"
                              >
                                <Edit3 className="h-3 w-3" /> Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteProduct(bizId, item.id)}
                                className="p-1 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-bold border border-red-500/20 flex items-center gap-1 cursor-pointer"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Tab 4: Analytics */}
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white font-display">Engagement Analytics</h2>
                <button
                  onClick={() => user && fetchListings(user.email)}
                  disabled={loadingListings}
                  className="bg-slate-900 border border-slate-800 hover:border-slate-700 text-xs font-bold text-slate-300 hover:text-white px-3.5 py-2 rounded-xl transition flex items-center gap-1.5 cursor-pointer shadow-sm"
                >
                  <span className={`material-symbols-outlined text-sm ${loadingListings ? 'animate-spin' : ''}`}>sync</span>
                  <span>{loadingListings ? 'Refreshing...' : 'Refresh Metrics'}</span>
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-slate-900/40 border border-slate-800 p-4 rounded-3xl text-left shadow-sm">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Views</div>
                  <div className="text-2xl font-extrabold text-white mt-1">{analyticsSummary.views.toLocaleString()}</div>
                </div>
                <div className="bg-slate-900/40 border border-slate-800 p-4 rounded-3xl text-left shadow-sm">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Call Leads</div>
                  <div className="text-2xl font-extrabold text-white mt-1">{analyticsSummary.calls.toLocaleString()}</div>
                </div>
                <div className="bg-slate-900/40 border border-slate-800 p-4 rounded-3xl text-left shadow-sm">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">WhatsApp Clicks</div>
                  <div className="text-2xl font-extrabold text-white mt-1">{analyticsSummary.whatsapp.toLocaleString()}</div>
                </div>
                <div className="bg-slate-900/40 border border-slate-800 p-4 rounded-3xl text-left shadow-sm">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Directions</div>
                  <div className="text-2xl font-extrabold text-white mt-1">{analyticsSummary.directions.toLocaleString()}</div>
                </div>
                <div className="bg-slate-900/40 border border-slate-800 p-4 rounded-3xl text-left shadow-sm">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Enquiries</div>
                  <div className="text-2xl font-extrabold text-white mt-1">{analyticsSummary.enquiries.toLocaleString()}</div>
                </div>
              </div>

              <div className="bg-slate-900/20 border border-slate-800 rounded-3xl p-5 shadow-sm overflow-x-auto">
                <h3 className="text-xs font-bold text-white uppercase tracking-wider mb-4">Per Listing Metrics</h3>
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-400 font-bold uppercase tracking-wider">
                      <th className="py-2.5">Business Name</th>
                      <th className="py-2.5 text-center">City</th>
                      <th className="py-2.5 text-right">Views</th>
                      <th className="py-2.5 text-right">Calls</th>
                      <th className="py-2.5 text-right">WhatsApp</th>
                      <th className="py-2.5 text-right">Directions</th>
                      <th className="py-2.5 text-right">Enquiries</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listings.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-4 text-center text-slate-500 font-semibold">No businesses to track yet.</td>
                      </tr>
                    ) : (
                      listings.map(l => (
                        <tr key={l.id} className="border-b border-slate-800/50 hover:bg-slate-900/20">
                          <td className="py-3 font-semibold text-white">{l.name}</td>
                          <td className="py-3 text-center text-slate-400">{l.city}</td>
                          <td className="py-3 text-right font-mono text-white">{l.view_count}</td>
                          <td className="py-3 text-right font-mono text-slate-300">{l.call_clicks}</td>
                          <td className="py-3 text-right font-mono text-slate-300">{l.whatsapp_clicks}</td>
                          <td className="py-3 text-right font-mono text-slate-300">{l.direction_clicks}</td>
                          <td className="py-3 text-right font-mono text-slate-300">{l.enquiries}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

            </div>
          )}

          {/* Tab 5: Change Password */}
          {activeTab === 'change_password' && (
            <div className="space-y-6 max-w-xl">
              <div>
                <h2 className="text-xl font-bold text-white font-display">Change Account Password</h2>
                <p className="text-xs text-slate-400 mt-1">Update your password to keep your business account secure.</p>
              </div>

              {dashPassError && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-2xl flex items-center gap-2 text-xs font-bold text-red-300">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-red-400" />
                  <span>{dashPassError}</span>
                </div>
              )}

              {dashPassSuccess && (
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl flex items-center gap-2 text-xs font-bold text-emerald-300">
                  <CheckCircle className="h-4 w-4 shrink-0 text-emerald-400" />
                  <span>{dashPassSuccess}</span>
                </div>
              )}

              <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-4">
                <div className="p-3.5 bg-slate-950 border border-slate-800 rounded-2xl flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white font-black text-sm flex items-center justify-center uppercase shrink-0">
                    {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                  </div>
                  <div className="min-w-0">
                    <div className="font-extrabold text-xs text-white truncate">{user?.name}</div>
                    <div className="text-[11px] text-slate-400 truncate">{user?.email}</div>
                  </div>
                </div>

                <form onSubmit={handleDashChangePassword} className="space-y-4 pt-2">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">New Password (min 6 characters) *</label>
                    <input
                      type="password"
                      value={dashNewPass}
                      onChange={(e) => setDashNewPass(e.target.value)}
                      required
                      minLength={6}
                      placeholder="••••••••"
                      className="w-full px-4 py-3 bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl text-sm font-semibold outline-none text-white transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">Confirm New Password *</label>
                    <input
                      type="password"
                      value={dashConfirmPass}
                      onChange={(e) => setDashConfirmPass(e.target.value)}
                      required
                      minLength={6}
                      placeholder="••••••••"
                      className="w-full px-4 py-3 bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl text-sm font-semibold outline-none text-white transition"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={dashPassLoading}
                    className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs py-3 rounded-xl transition shadow-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    <Lock className="h-4 w-4" />
                    <span>{dashPassLoading ? 'Updating Password...' : 'Save New Password'}</span>
                  </button>
                </form>
              </div>
            </div>
          )}

        </main>

      </div>
    </div>
  );
}

// Simulated Inbox summary drawer component
function SimulatedInboxSummary({ toEmail }: { toEmail: string }) {
  const [inboxEmails, setInboxEmails] = useState<SimulatedEmail[]>([]);

  useEffect(() => {
    const load = () => {
      const savedEmails = localStorage.getItem(`emails_${toEmail}`);
      if (savedEmails) {
        setInboxEmails(JSON.parse(savedEmails) as SimulatedEmail[]);
      } else {
        setInboxEmails([]);
      }
    };
    load();
    const interval = setInterval(load, 2000);
    return () => clearInterval(interval);
  }, [toEmail]);

  if (inboxEmails.length === 0) {
    return (
      <div className="py-4 text-center text-xs text-slate-500 font-semibold">
        No verification email received yet. Keep this drawer open to inspect OTP activation.
      </div>
    );
  }

  const latest = inboxEmails[0];

  return (
    <div className="bg-slate-950 p-4 border border-slate-800 rounded-2xl text-left space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-primary font-bold uppercase tracking-wider">{latest.subject}</span>
        <span className="text-[9px] text-slate-500 font-bold">{latest.sentAt}</span>
      </div>
      <pre className="text-xs text-slate-300 whitespace-pre-wrap font-medium font-body leading-relaxed max-h-32 overflow-y-auto pr-1">
        {latest.body}
      </pre>
      <div className="border-t border-slate-800 pt-2 flex justify-between text-[10px] font-bold text-slate-400">
        <span>To: {latest.to}</span>
        {latest.subject.includes('Verify') && (
          <a
            {...linkProps(`/owner?activate=${latest.body.match(/OTP is:\s*(\d+)/)?.[1] || ''}`)}
            className="text-primary hover:underline flex items-center gap-0.5"
          >
            Click here to verify account <ArrowRight className="h-3 w-3" />
          </a>
        )}
      </div>
    </div>
  );
}
